<template>
  <div class="drug-inventory">
    <!-- 步骤导航 -->
    <div class="step-nav">
      <el-steps :active="stepActive" align-center finish-status="success">
        <el-step title="药品盘点列表" description="查看和管理盘点任务"></el-step>
        <el-step :title="step2Title" description="执行药品盘点操作"></el-step>
        <el-step title="审核结转" description="审核确认盘点结果"></el-step>
      </el-steps>
    </div>

    <!-- 第一步：药品盘点列表 -->
    <div v-show="currentStep === 1" class="step-content step1">
      <div class="table-header">
        <div class="header-actions">
          <el-button type="success" icon="el-icon-plus" size="mini" @click="handleCreateTask">创建盘点任务</el-button>
          <el-button type="warning" icon="el-icon-refresh" size="mini" @click="loadStockList">刷新</el-button>
        </div>
      </div>

      <!-- 查询条件 -->
      <div class="filter-container">
        <div class="filter-title">
          <span class="title-text">药品盘点列表</span>
        </div>
        <el-form :model="queryForm" inline class="query-form">
          <el-form-item label="盘点类型：" class="form-item-center">
            <el-select v-model="queryForm.stockType" placeholder="全部类型" size="mini" clearable
              @change="handleFilterChange" style="width: 120px;">
              <el-option label="药库" :value="1"></el-option>
              <el-option label="药房" :value="2"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="单据状态：" class="form-item-center">
            <el-select v-model="queryForm.status" placeholder="全部状态" size="mini" clearable
              @change="handleFilterChange" style="width: 120px;">
              <el-option label="草稿" :value="0"></el-option>
              <el-option label="已提交" :value="1"></el-option>
              <el-option label="已审核" :value="2"></el-option>
              <el-option label="已结转" :value="3"></el-option>
              <el-option label="反结转" :value="4"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item class="form-item-center">
            <el-input v-model="queryForm.keyword" placeholder="搜索单据编号/备注" @keyup.enter.native="handleSearch"
              style="width: 200px;" size="mini">
              <el-button slot="append" icon="el-icon-Ssearch" @click="handleSearch" size="mini"></el-button>
            </el-input>
          </el-form-item>
        </el-form>
      </div>

      <!-- 盘点任务表格 -->
      <div class="table-container">
        <el-table :data="stockList" v-loading="tableLoading" stripe border style="width: 100%;"
          :scrollable="true" ref="stockTable" class="custom-table" row-key="id">
          <el-table-column type="index" label="序号" width="60" align="center" fixed="left"></el-table-column>
          <el-table-column prop="docNum" label="单据编号" width="180" show-overflow-tooltip></el-table-column>
          <el-table-column prop="stockType" label="盘点药库" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStockTypeTagType(scope.row.stockType)" size="mini">
                {{ getStockTypeText(scope.row.stockType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="单据状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.status)" size="mini">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="stockDate" label="账面日期" width="160">
            <template slot-scope="scope">{{ formatDateTime(scope.row.stockDate) }}</template>
          </el-table-column>

          <el-table-column prop="profitPurcSumAmt" label="盘点盈亏成本金额合计" width="160" align="right">
            <template slot-scope="scope">{{ scope.row.profitPurcSumAmt }}</template>
          </el-table-column>
          <el-table-column prop="profitSaleSumAmt" label="盘点盈亏零售金额合计" width="160" align="right">
            <template slot-scope="scope">{{ scope.row.profitSaleSumAmt }}</template>
          </el-table-column>

          <el-table-column prop="createName" label="创建人" width="100"></el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="160">
            <template slot-scope="scope">{{ formatDateTime(scope.row.createTime) }}</template>
          </el-table-column>
          <el-table-column prop="reviewName" label="审核人" width="100"></el-table-column>
          <el-table-column prop="reviewTime" label="审核时间" width="160">
            <template slot-scope="scope">
              {{ scope.row.reviewTime ? formatDateTime(scope.row.reviewTime) : '--' }}
            </template>
          </el-table-column>
          <el-table-column prop="passingOverName" label="结转人" width="100"></el-table-column>
          <el-table-column prop="passingOverTime" label="结转时间" width="160">
            <template slot-scope="scope">
              {{ scope.row.passingOverTime ? formatDateTime(scope.row.passingOverTime) : '--' }}
            </template>
          </el-table-column>

          <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip></el-table-column>

          <!-- 操作列 -->
          <el-table-column label="操作" width="200" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="operation-buttons">
                <el-button type="text" size="mini" @click="handleDetail(scope.row)">详情</el-button>
                <template v-if="scope.row.status === 0">
                  <el-button type="text" size="mini" @click="handleEdit(scope.row)">修改</el-button>
                  <el-button type="text" size="mini" @click="handleSubmit(scope.row)">提交</el-button>
                  <el-button type="text" size="mini" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
                </template>
                <template v-else-if="scope.row.status === 1">
                  <el-button type="text" size="mini" @click="handleAudit(scope.row)">去审核</el-button>
                </template>
                <template v-else-if="scope.row.status === 2">
                  <el-button type="text" size="mini" @click="handleCarryOver(scope.row)">去结转</el-button>
                </template>
                <template v-else-if="scope.row.status === 3">
                  <el-button type="text" size="mini" class="reverse-btn" @click="handleReverseCarryOver(scope.row)">反结转</el-button>
                </template>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <!-- 自定义合计行 -->
        <div v-if="stockList.length > 0" class="custom-summary-row">
          <div class="summary-content">
            <div class="summary-left">
              <span class="summary-label">合计</span>
              <span class="summary-item">
                <label>库存成本合计金额：</label>
                <span class="summary-amount">{{ formatCurrencyWithComma(totalTablePurcSumAmt) }}</span>
              </span>
              <span class="summary-item">
                <label>库存零售合计金额：</label>
                <span class="summary-amount">{{ formatCurrencyWithComma(totalTableSaleSumAmt) }}</span>
              </span>
            </div>
            <div class="summary-right">
              <el-pagination @size-change="handleSizeChange" @current-change="handlePageChange"
                :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]"
                :page-size="pagination.pageSize" layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total" class="summary-pagination">
              </el-pagination>
            </div>
          </div>
        </div>
        <!-- 空数据提示 -->
        <div v-if="stockList.length === 0 && !tableLoading" class="empty-data">
          <el-empty description="暂无盘点任务数据"></el-empty>
        </div>
      </div>
    </div>

    <!-- 创建盘点任务对话框 -->
    <el-dialog title="创建盘点任务" :visible.sync="createDialogVisible" width="400px" :close-on-click-modal="false">
      <el-form :model="createForm" :rules="createRules" ref="createForm" label-width="80px">
        <el-form-item label="单据编号">
          <el-input v-model="createForm.docNum" disabled placeholder="系统自动生成" style="width: 100%">
            <template slot="append">
              <el-button icon="el-icon-refresh" @click="generateDocNumber" title="重新生成"></el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="账面日期">
          <el-date-picker v-model="createForm.bookDate" type="date" placeholder="选择日期"
            value-format="yyyy-MM-dd" style="width: 100%" @change="generateDocNumber">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="盘点药库" prop="warehouseId">
          <el-select v-model="createForm.warehouseId" placeholder="请选择要盘点的药库" style="width: 100%"
            @change="handleWarehouseChange">
            <el-option label="药库" :value="1"></el-option>
            <el-option label="药房" :value="2"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreateConfirm" :loading="createLoading">确认</el-button>
      </div>
    </el-dialog>

    <!-- 第二步：盘点执行 -->
    <div v-show="currentStep === 2" class="step-content step2">
      <div class="step2-header">
        <div class="header-left">
          <span class="task-title">当前盘点任务</span>
          <div class="action-buttons">
            <el-button size="mini" type="primary" icon="el-icon-edit" @click="handleManualInput">手工录入</el-button>
            <el-button size="mini" icon="el-icon-delete-solid" @click="handleClearAll">清空</el-button>
          </div>
        </div>
        <div class="header-right">
          <el-button-group class="filter-buttons" style="display: flex; align-items: center;">
            <el-button size="mini" :type="filterType === 'all' ? 'primary' : ''"
              @click="handleFilterTypeChange('all')">
              全部药品
              <el-badge v-if="inventoryItems.length > 0" :value="inventoryItems.length" :max="99"
                class="item" style="position: absolute; top: -8px; right: -8px;"></el-badge>
            </el-button>
            <el-button size="mini" :type="filterType === 'diff' ? 'primary' : ''"
              @click="handleFilterTypeChange('diff')">
              差异药品
              <el-badge v-if="diffDrugCount > 0" :value="diffDrugCount" :max="99"
                class="item" type="warning" style="position: absolute; top: -8px; right: -8px;"></el-badge>
            </el-button>
          </el-button-group>
        </div>
      </div>

      <!-- 双表格容器 -->
      <div class="dual-tables-container" :class="{ 'task-tables-show': showTaskTables }">
        <!-- 左侧主表格 -->
        <div class="main-table-container">
          <div class="table-wrapper" style="position: relative; overflow: hidden;">
            <div class="table-container-inner" style="width: 100%; overflow-x: auto; overflow-y: hidden;">
              <el-table :data="filteredInventoryItems"
                style="width: 100%; min-width: 100%;"
                height="400"
                v-loading="tableLoading"
                @selection-change="handleSelectionChange"
                :key="'main-table-' + filterType"
                class="fixed-column-table"
                :row-style="{ height: '20px' }"
                :cell-style="{ padding: '2px 0' }"
                row-key="id">
                <el-table-column type="index" label="序号" width="60" align="center">
                  <template slot-scope="scope">
                    <el-tooltip v-if="isDiffDrug(scope.row)" effect="dark"
                      :content="getDiffDrugTip(scope.row)" placement="top">
                      <span class="diff-indicator">●</span>
                    </el-tooltip>
                    <span>{{ scope.$index + 1 }}</span>
                  </template>
                </el-table-column>
                <!-- 新增类型列 - 显示药品类型（西药/中药/卫材） -->
                <el-table-column label="类型" width="100" align="center" fixed="left">
                  <template slot-scope="scope">
                    <el-tag :type="getDrugTypeTagType(scope.row.drugType)" size="mini">
                      {{ getDrugTypeText(scope.row.drugType) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="drugCode" label="药品编码" width="120" show-overflow-tooltip></el-table-column>
                <el-table-column prop="drugName" label="药品名称" width="150" show-overflow-tooltip></el-table-column>
                <el-table-column prop="drugSpec" label="规格" width="120" show-overflow-tooltip></el-table-column>
                <el-table-column prop="manuLotnum" label="生产批号" width="120"></el-table-column>

                <el-table-column label="账面数量" width="180" align="center">
                  <el-table-column prop="tablePacCnt" label="包装单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span class="number-text">{{ formatNumber(scope.row.tablePacCnt) + (scope.row.pacUnt || '') }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="tableMinCnt" label="最小单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span class="number-text">{{ formatNumber(scope.row.tableMinCnt) + (scope.row.minUnt || '') }}</span>
                    </template>
                  </el-table-column>
                </el-table-column>

                <el-table-column label="盘点数量" width="180" align="center">
                  <el-table-column prop="stockPacCnt" label="包装单位" width="100">
                    <template slot-scope="scope">
                      <div v-if="scope.row.editingPac" class="editing-cell">
                        <el-input
                          v-model="scope.row.stockPacCnt"
                          size="mini"
                          type="number"
                          :min="0"
                          @blur="handleTableQuantityBlur(scope.row, 'pac')"
                          @change="calculateTableProfitLoss(scope.row)"
                          class="quantity-input"
                          v-focus
                          @keyup.enter.native="finishTableEditing(scope.row, 'pac')">
                        </el-input>
                      </div>
                      <div v-else class="view-cell" @dblclick="handleTableCellDblClick(scope.row, 'pac')">
                        {{ formatNumber(scope.row.stockPacCnt) }}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="stockMinCnt" label="最小单位" width="100">
                    <template slot-scope="scope">
                      <div v-if="scope.row.editingMin" class="editing-cell">
                        <el-input
                          v-model="scope.row.stockMinCnt"
                          size="mini"
                          type="number"
                          :min="0"
                          @blur="handleTableQuantityBlur(scope.row, 'min')"
                          @change="calculateTableProfitLoss(scope.row)"
                          class="quantity-input"
                          v-focus
                          @keyup.enter.native="finishTableEditing(scope.row, 'min')">
                        </el-input>
                      </div>
                      <div v-else class="view-cell" @dblclick="handleTableCellDblClick(scope.row, 'min')">
                        {{ formatNumber(scope.row.stockMinCnt) }}
                      </div>
                    </template>
                  </el-table-column>
                </el-table-column>

                <el-table-column label="盈亏数量" width="180" align="center">
                  <el-table-column prop="profitPacCnt" label="包装单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span :class="getProfitLossClass(scope.row.profitPacCnt)" class="number-text">{{ formatNumber(scope.row.profitPacCnt) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="profitMinCnt" label="最小单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span :class="getProfitLossClass(scope.row.profitMinCnt)" class="number-text">{{ formatNumber(scope.row.profitMinCnt) }}</span>
                    </template>
                  </el-table-column>
                </el-table-column>

                <el-table-column label="成本单价" width="160" align="center">
                  <el-table-column prop="maxPurcPric" label="包装单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span class="price-text">{{ formatCurrency(scope.row.maxPurcPric) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="minPurcPric" label="最小单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span class="price-text">{{ formatCurrency(scope.row.minPurcPric) }}</span>
                    </template>
                  </el-table-column>
                </el-table-column>

                <el-table-column label="销售单价" width="160" align="center">
                  <el-table-column prop="maxSalePric" label="包装单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span class="price-text">{{ formatCurrency(scope.row.maxSalePric) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="minSalePric" label="最小单位" width="100" align="right">
                    <template slot-scope="scope">
                      <span class="price-text">{{ formatCurrency(scope.row.minSalePric) }}</span>
                    </template>
                  </el-table-column>
                </el-table-column>

                <el-table-column prop="stockPurcAmt" label="账面成本金额" width="120" align="right">
                  <template slot-scope="scope">{{ formatCurrency(scope.row.tablePurcAmt) }}</template>
                </el-table-column>
                <el-table-column prop="tablePurcAmt" label="盘点成本金额" width="120" align="right">
                  <template slot-scope="scope">{{ formatCurrency(scope.row.stockPurcAmt) }}</template>
                </el-table-column>
                <el-table-column prop="profitPurcAmt" label="盈亏成本金额" width="120" align="right">
                  <template slot-scope="scope">
                    <span :class="getProfitLossClass(scope.row.profitPurcAmt)">{{ scope.row.profitPurcAmt }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="profitSaleAmt" label="盈亏销售金额" width="120" align="right">
                  <template slot-scope="scope">
                    <span :class="getProfitLossClass(scope.row.profitSaleAmt)">{{ scope.row.profitSaleAmt }}</span>
                  </template>
                </el-table-column>

                <el-table-column label="系统信息" width="210" align="center">
                  <el-table-column prop="drugId" label="药品ID" width="70" align="center">
                    <template slot-scope="scope"><span class="id-text">{{ scope.row.drugId || '-' }}</span></template>
                  </el-table-column>
                  <el-table-column prop="id" label="明细ID" width="70" align="center">
                    <template slot-scope="scope"><span class="id-text">{{ scope.row.id || '-' }}</span></template>
                  </el-table-column>
                  <el-table-column prop="stockId" label="盘点ID" width="70" align="center">
                    <template slot-scope="scope"><span class="id-text">{{ scope.row.stockId || '-' }}</span></template>
                  </el-table-column>
                </el-table-column>

                <el-table-column prop="prodentpName" label="生产厂家" width="150" show-overflow-tooltip></el-table-column>

                <el-table-column label="操作" width="120" fixed="right" align="center" class-name="operation-column">
                  <template slot-scope="scope">
                    <div class="operation-buttons-compact">
                      <el-button type="warning" size="mini" @click="editItem(scope.row)">明细记录</el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
          <div class="pagination">
            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
              :current-page="searchParams.pageNum" :page-sizes="[10, 20, 50, 100]"
              :page-size="searchParams.pageSize" layout="total, sizes, prev, pager, next, jumper"
              :total="total"></el-pagination>
          </div>
        </div>

        <!-- 右侧已盘任务表格 -->
        <div class="task-table-container" v-show="showTaskTables">
          <div class="table-header">
            <h4>已盘任务</h4>
            <div class="table-actions">
              <el-select v-model="taskTableStockType" placeholder="盘点类型" size="mini"
                style="width: 120px; margin-right: 10px;" @change="loadTaskTableData">
                <el-option label="药库" :value="1"></el-option>
                <el-option label="药房" :value="2"></el-option>
                <el-option label="卫材" :value="3"></el-option>
              </el-select>
            </div>
          </div>
          <div class="table-wrapper">
            <el-table :data="taskTableItems" height="400" style="width: 100%" v-loading="taskTableLoading"
              :scrollable="true" :row-style="{ height: '20px' }" :cell-style="{ padding: '2px 0' }"
              row-key="id">
              <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
              <el-table-column prop="drugCode" label="药品编码" width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="drugName" label="药品名称" width="150" show-overflow-tooltip></el-table-column>
              <el-table-column prop="drugSpec" label="规格" width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="manuLotnum" label="生产批号" width="120"></el-table-column>
              <el-table-column label="账面数量" width="180" align="center">
                <el-table-column prop="tablePacCnt" label="包装单位" width="100" align="right">
                  <template slot-scope="scope"><span class="number-text">{{ formatNumber(scope.row.tablePacCnt) }}</span></template>
                </el-table-column>
                <el-table-column prop="tableMinCnt" label="最小单位" width="100" align="right">
                  <template slot-scope="scope"><span class="number-text">{{ formatNumber(scope.row.tableMinCnt) }}</span></template>
                </el-table-column>
              </el-table-column>
              <el-table-column label="盘点数量" width="180" align="center">
                <el-table-column prop="stockPacCnt" label="包装单位" width="100" align="right">
                  <template slot-scope="scope"><span class="number-text">{{ formatNumber(scope.row.stockPacCnt) }}</span></template>
                </el-table-column>
                <el-table-column prop="stockMinCnt" label="最小单位" width="100" align="right">
                  <template slot-scope="scope"><span class="number-text">{{ formatNumber(scope.row.stockMinCnt) }}</span></template>
                </el-table-column>
              </el-table-column>
              <el-table-column label="盈亏数量" width="180" align="center">
                <el-table-column prop="profitPacCnt" label="包装单位" width="100" align="right">
                  <template slot-scope="scope">
                    <span :class="getProfitLossClass(scope.row.profitPacCnt)" class="number-text">{{ formatNumber(scope.row.profitPacCnt) }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="profitMinCnt" label="最小单位" width="100" align="right">
                  <template slot-scope="scope">
                    <span :class="getProfitLossClass(scope.row.profitMinCnt)" class="number-text">{{ formatNumber(scope.row.profitMinCnt) }}</span>
                  </template>
                </el-table-column>
              </el-table-column>
              <el-table-column prop="stockPurcAmt" label="账面成本金额" width="120" align="right">
                <template slot-scope="scope">{{ scope.row.stockPurcAmt }}</template>
              </el-table-column>
              <el-table-column prop="tablePurcAmt" label="盘点成本金额" width="120" align="right">
                <template slot-scope="scope">{{ scope.row.tablePurcAmt }}</template>
              </el-table-column>
              <el-table-column prop="profitPurcAmt" label="盈亏成本金额" width="120" align="right">
                <template slot-scope="scope">
                  <span :class="getProfitLossClass(scope.row.profitPurcAmt)">{{ scope.row.profitPurcAmt }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="prodentpName" label="生产厂家" width="150" show-overflow-tooltip></el-table-column>
              <el-table-column prop="createTime" label="盘点时间" width="160">
                <template slot-scope="scope">{{ formatDateTime(scope.row.createTime) }}</template>
              </el-table-column>
            </el-table>
          </div>
          <div v-if="taskTableItems.length === 0 && !taskTableLoading" class="empty-data">
            <el-empty description="暂无已盘任务数据"></el-empty>
          </div>
        </div>
      </div>

      <div class="step-actions">
        <el-button size="mini" @click="currentStep = 1">上一步</el-button>
        <el-button size="mini" type="primary" @click="handleComplete">完成盘点</el-button>
      </div>
    </div>

    <!-- 第三步：审核结转 -->
    <div v-show="currentStep === 3" class="step-content step3">
      <div class="inventory-header">
        <div class="header-left">
          <span class="document-number">盘点编号 {{ docNumber }}</span>
          <el-tag :type="getStatusTagType(currentStatus)">{{ getStatusText(currentStatus) }}</el-tag>
          <span>{{ getStockTypeText(selectedWarehouseType) }}</span>
        </div>
        <div class="header-actions">
          <el-button size="mini" icon="el-icon-printer" @click="handlePrint">打印</el-button>
          <el-button size="mini" icon="el-icon-download" @click="handleExport">导出</el-button>
          <el-button size="mini" icon="el-icon-notebook-2" @click="handleHistory">历史单据</el-button>
        </div>
      </div>

      <div class="search-bar">
        <el-input placeholder="搜索条码/编码/名称/规格/别名" class="search-input" size="mini">
          <el-button slot="append" icon="el-icon-search"></el-button>
        </el-input>
        <el-button-group class="filter-buttons">
          <el-button size="mini" :type="filterType === 'all' ? 'primary' : ''" @click="filterType = 'all'">全部药品</el-button>
          <el-button size="mini" :type="filterType === 'diff' ? 'primary' : ''" @click="filterType = 'diff'">差异药品</el-button>
        </el-button-group>
        <div class="summary-info">
          <span>盘盈 {{ totalProfitCount }}</span>
          <span>盘亏 {{ totalLossCount }}</span>
          <span>盈亏金额: {{ formatCurrency(totalProfitAmount) }}</span>
        </div>
      </div>

      <div class="inventory-table">
        <el-table :data="completedItems" height="400" style="width: 100%" :scrollable="true"
          :row-style="{ height: '20px' }" :cell-style="{ padding: '2px 0' }" row-key="id">
          <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
          <el-table-column prop="drugCode" label="药品编码" width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="drugName" label="药品名称" width="150" show-overflow-tooltip></el-table-column>
          <el-table-column prop="drugSpec" label="规格" width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="minUnt" label="最小单位" width="100"></el-table-column>
          <el-table-column prop="manuLotnum" label="生产批号" width="120"></el-table-column>
          <el-table-column prop="tableMinCnt" label="账面数量(最小单位)" width="120" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.tableMinCnt) }}</template>
          </el-table-column>
          <el-table-column prop="stockMinCnt" label="盘点数量(最小单位)" width="120" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.stockMinCnt) }}</template>
          </el-table-column>
          <el-table-column prop="profitMinCnt" label="盈亏数量(最小单位)" width="140" align="right">
            <template slot-scope="scope">
              <span :class="getProfitLossClass(scope.row.profitMinCnt)">{{ formatNumber(scope.row.profitMinCnt) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="profitPurcAmt" label="盈亏金额(成本)" width="120" align="right">
            <template slot-scope="scope">
              <span :class="getProfitLossClass(scope.row.profitPurcAmt)">{{ formatCurrency(scope.row.profitPurcAmt) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="profitSaleAmt" label="盈亏金额(销售)" width="120" align="right">
            <template slot-scope="scope">
              <span :class="getProfitLossClass(scope.row.profitSaleAmt)">{{ formatCurrency(scope.row.profitSaleAmt) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="handleEditCompletedItem(scope.row)">修改</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="table-footer">
          <div class="table-summary">
            <div class="summary-row">
              <span>合计:</span>
              <span>{{ formatCurrency(totalStockAmount) }}</span>
              <span>{{ formatCurrency(totalTableAmount) }}</span>
              <span :class="getProfitLossClass(totalProfitAmount)">{{ formatCurrency(totalProfitAmount) }}</span>
            </div>
          </div>
          <div class="pagination">
            <el-pagination :current-page="1" :page-size="50" :total="completedItems.length"
              layout="prev, pager, next, jumper, sizes, total"></el-pagination>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <el-button size="mini" @click="handleModify">修改</el-button>
        <el-button size="mini" @click="handleDelete" style="color: #F56C6C;">删除</el-button>
        <el-button size="mini" type="primary" @click="handleAudit" :disabled="isAuditDisabled" :loading="auditLoading">
          {{ auditButtonText }}
        </el-button>
        <el-button v-if="showReverseButton" size="mini" type="warning" @click="handleReverseCarryOver" :loading="reverseCarryOverLoading">
          反结转
        </el-button>
      </div>
    </div>

    <!-- 详情模态框 -->
    <el-dialog title="盘点任务详情" :visible.sync="detailDialogVisible" width="90%" :close-on-click-modal="false">
      <div v-if="currentDetail" class="detail-content">
        <div class="detail-header">
          <div class="header-info">
            <span class="doc-num">单据编号：{{ currentDetail.docNum }}</span>
            <el-tag :type="getStatusTagType(currentDetail.status)" size="small">
              {{ getStatusText(currentDetail.status) }}
            </el-tag>
            <el-tag :type="getStockTypeTagType(currentDetail.stockType)" size="small">
              {{ getStockTypeText(currentDetail.stockType) }}
            </el-tag>
          </div>
        </div>
        <div class="detail-body">
          <el-table :data="detailItems" v-loading="detailLoading" stripe border style="width: 100%" height="400"
            :row-style="{ height: '20px' }" :cell-style="{ padding: '2px 0' }" row-key="id">
            <el-table-column type="index" label="序号" width="60" align="center" fixed="left"></el-table-column>
            <!-- 详情表格类型列 - 显示盘点药库类型（药库/药房/卫材） -->
            <el-table-column label="类型" width="100" align="center" fixed="left">
              <template slot-scope="scope">
                <el-tag :type="getStockTypeTagType(scope.row.stockType || scope.row.drugType)" size="mini">
                  {{ getStockTypeText(scope.row.stockType || scope.row.drugType) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="drugCode" label="药品编码" width="160" show-overflow-tooltip></el-table-column>
            <el-table-column prop="drugName" label="药品名称" width="150" show-overflow-tooltip></el-table-column>
            <el-table-column prop="drugSpec" label="规格" width="120" show-overflow-tooltip></el-table-column>
            <el-table-column prop="manuLotnum" label="生产批号" width="120"></el-table-column>
            <el-table-column prop="stockMinCnt" label="盘点最小单位数量" width="140" align="right">
              <template slot-scope="scope">{{ formatNumber(scope.row.stockMinCnt) }}</template>
            </el-table-column>
            <el-table-column prop="stockPacCnt" label="盘点包装单位数量" width="140" align="right">
              <template slot-scope="scope">{{ formatNumber(scope.row.stockPacCnt) }}</template>
            </el-table-column>
            <el-table-column prop="profitMinCnt" label="盈亏最小单位数量" width="140" align="right">
              <template slot-scope="scope">
                <span :class="getProfitLossClass(scope.row.profitMinCnt)">{{ formatNumber(scope.row.profitMinCnt) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="profitPacCnt" label="盈亏包装单位数量" width="140" align="right">
              <template slot-scope="scope">
                <span :class="getProfitLossClass(scope.row.profitPacCnt)">{{ formatNumber(scope.row.profitPacCnt) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="tablePurcAmt" label="账面成本金额" width="120" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.tablePurcAmt) }}</template>
            </el-table-column>
            <el-table-column prop="tableSaleAmt" label="账面零售金额" width="120" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.tableSaleAmt) }}</template>
            </el-table-column>
            <el-table-column prop="stockPurcAmt" label="盘点成本金额" width="120" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.stockPurcAmt) }}</template>
            </el-table-column>
            <el-table-column prop="stockSaleAmt" label="盘点零售金额" width="120" align="right">
              <template slot-scope="scope">{{ formatCurrency(scope.row.stockSaleAmt) }}</template>
            </el-table-column>
            <el-table-column prop="profitPurcAmt" label="盈亏成本金额" width="120" align="right">
              <template slot-scope="scope">
                <span :class="getProfitLossClass(scope.row.profitPurcAmt)">{{ scope.row.profitPurcAmt }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="profitSaleAmt" label="盈亏零售金额" width="120" align="right">
              <template slot-scope="scope">
                <span :class="getProfitLossClass(scope.row.profitSaleAmt)">{{ scope.row.profitSaleAmt }}</span>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="detailItems.length === 0 && !detailLoading" class="empty-detail">
            <el-empty description="暂无盘点明细数据"></el-empty>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 手工录入模态框 -->
    <el-dialog title="选择盘点药品" :visible.sync="manualInputDialogVisible" width="95%" top="5vh"
      :close-on-click-modal="false" class="manual-input-dialog">
      <div class="manual-input-content">
        <div class="search-area">
          <el-select v-model="manualSearchParams.drugName" placeholder="请选择药品名称（按回车键快速添加）" size="mini"
            clearable filterable remote :remote-method="handleManualDrugSearch"
            :loading="manualDrugSearchLoading" style="width: 400px; margin-right: 15px; flex-shrink: 0;"
            @change="handleManualDrugChange" @keyup.enter.native="handleManualKeyPress"
            ref="manualSearchInput">
            <el-option-group v-for="group in manualDrugOptions" :key="group.label">
              <el-option disabled class="drug-title-option" :value="null">
                <span style="font-weight: bold; color: #409EFF;">{{ group.label }}</span>
              </el-option>
              <el-option v-for="item in group.options" :key="item.drugId + '_' + item.manuLotnum"
                :label="getBatchOptionLabel(item)" :value="item.drugName">
                <div class="batch-option-content">
                  <div class="batch-main-info" style="font-size:12px;">
                    <span class="batch-label">批次号：{{ item.manuLotnum || '无批次' }}</span>
                    <span class="batch-label" style="margin-left:8px;">规格：{{ item.drugSpec || '无规格' }}</span>
                    <span style="margin-left:8px;" class="batch-spec" v-if="item.prodentpName">厂家：{{ item.prodentpName }}</span>
                  </div>
                  <div class="batch-hint" style="font-size:11px; color:#67C23A;">按回车键快速添加</div>
                </div>
              </el-option>
            </el-option-group>
          </el-select>
          <el-button type="primary" size="mini" @click="handleAddToManualTable" :disabled="!manualSearchParams.drugName" style="flex-shrink: 0;">
            <i class="el-icon-plus"></i> 添加到盘点表
          </el-button>
          <el-input
            v-model="traceCodeInput"
            placeholder="扫描或输入追溯码"
            size="mini"
            style="width: 260px; margin-left: auto; flex-shrink: 0;"
            clearable
            @keyup.enter.native="handleScanTraceCode"
          >
            <el-button slot="append" icon="el-icon-Ssearch" @click="handleScanTraceCode">查询</el-button>
          </el-input>
          <div class="shortcut-hint" style="flex-shrink: 0; margin-left: 15px;">
            <span class="hint-text">💡 提示：选择药品后按 Enter 键可快速添加到盘点表</span>
          </div>
        </div>

        <!-- 盘点药品表格 -->
        <div class="manual-table-container">
          <div class="table-header">
            <h4>盘点药品列表</h4>
          </div>
          <div class="table-wrapper" style="position: relative;">
            <el-table :data="filteredManualTableItems" style="width: 100%" empty-text=" "
              v-loading="manualTableLoading" :scrollable="true" class="manual-data-table"
              :row-style="{ height: '20px' }" :cell-style="{ padding: '2px 0' }"
              row-key="id">
              <el-table-column type="index" label="序号" width="60" align="center">
                <template slot-scope="scope">
                  <el-tooltip v-if="isDiffDrug(scope.row)" effect="dark"
                    :content="getDiffDrugTip(scope.row)" placement="top">
                    <span class="diff-indicator">●</span>
                  </el-tooltip>
                  <span>{{ scope.$index + 1 }}</span>
                </template>
              </el-table-column>
              <!-- 手工录入表格也添加类型列，保持一致性 -->
              <el-table-column label="类型" width="100" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getDrugTypeTagType(scope.row.drugType)" size="mini">
                    {{ getDrugTypeText(scope.row.drugType) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="drugCode" label="药品编码" width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="drugName" label="药品名称" width="150" show-overflow-tooltip></el-table-column>
              <el-table-column prop="drugSpec" label="规格" width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="manuLotnum" label="生产批号" width="120"></el-table-column>

              <el-table-column label="账面数量" width="180" align="center">
                <el-table-column prop="tablePacCnt" label="包装单位" width="100" align="right">
                  <template slot-scope="scope">
                    <span class="number-text">{{ formatNumber(scope.row.tablePacCnt) + (scope.row.pacUnt || '') }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="tableMinCnt" label="最小单位" width="100" align="right">
                  <template slot-scope="scope">
                    <span class="number-text">{{ formatNumber(scope.row.tableMinCnt) + (scope.row.minUnt || '') }}</span>
                  </template>
                </el-table-column>
              </el-table-column>

              <el-table-column label="盘点数量" width="180" align="center">
                <el-table-column prop="stockPacCnt" label="包装单位" width="100">
                  <template slot-scope="scope">
                    <el-input v-model="scope.row.stockPacCnt" size="mini" type="number" :min="0"
                      @blur="handleManualQuantityBlur(scope.row, 'pac')"
                      @change="calculateManualProfitLoss(scope.row)" class="quantity-input"></el-input>
                  </template>
                </el-table-column>
                <el-table-column prop="stockMinCnt" label="最小单位" width="100">
                  <template slot-scope="scope">
                    <el-input v-model="scope.row.stockMinCnt" size="mini" type="number" :min="0"
                      @blur="handleManualQuantityBlur(scope.row, 'min')"
                      @change="calculateManualProfitLoss(scope.row)" class="quantity-input"></el-input>
                  </template>
                </el-table-column>
              </el-table-column>

              <el-table-column label="盈亏数量" width="180" align="center">
                <el-table-column prop="profitPacCnt" label="包装单位" width="100" align="right">
                  <template slot-scope="scope">
                    <span :class="getProfitLossClass(scope.row.profitPacCnt)" class="number-text">{{ formatNumber(scope.row.profitPacCnt) }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="profitMinCnt" label="最小单位" width="100" align="right">
                  <template slot-scope="scope">
                    <span :class="getProfitLossClass(scope.row.profitMinCnt)" class="number-text">{{ formatNumber(scope.row.profitMinCnt) }}</span>
                  </template>
                </el-table-column>
              </el-table-column>

              <el-table-column label="成本单价" width="160" align="center">
                <el-table-column prop="maxPurcPric" label="包装单位" width="100" align="right">
                  <template slot-scope="scope"><span class="price-text">{{ formatCurrency(scope.row.maxPurcPric) }}</span></template>
                </el-table-column>
                <el-table-column prop="minPurcPric" label="最小单位" width="100" align="right">
                  <template slot-scope="scope"><span class="price-text">{{ formatCurrency(scope.row.minPurcPric) }}</span></template>
                </el-table-column>
              </el-table-column>

              <el-table-column label="销售单价" width="160" align="center">
                <el-table-column prop="maxSalePric" label="包装单位" width="100" align="right">
                  <template slot-scope="scope"><span class="price-text">{{ formatCurrency(scope.row.maxSalePric) }}</span></template>
                </el-table-column>
                <el-table-column prop="minSalePric" label="最小单位" width="100" align="right">
                  <template slot-scope="scope"><span class="price-text">{{ formatCurrency(scope.row.minSalePric) }}</span></template>
                </el-table-column>
              </el-table-column>

              <el-table-column prop="tablePurcAmt" label="账面成本金额" width="120" align="right">
                <template slot-scope="scope">{{ formatCurrency(scope.row.tablePurcAmt) }}</template>
              </el-table-column>
              <el-table-column prop="stockPurcAmt" label="盘点成本金额" width="120" align="right">
                <template slot-scope="scope">{{ formatCurrency(scope.row.stockPurcAmt) }}</template>
              </el-table-column>
              <el-table-column prop="profitPurcAmt" label="盈亏成本金额" width="120" align="right">
                <template slot-scope="scope">
                  <span :class="getProfitLossClass(scope.row.profitPurcAmt)">{{ scope.row.profitPurcAmt }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="profitSaleAmt" label="盈亏销售金额" width="120" align="right">
                <template slot-scope="scope">
                  <span :class="getProfitLossClass(scope.row.profitSaleAmt)">{{ scope.row.profitSaleAmt }}</span>
                </template>
              </el-table-column>

              <el-table-column prop="prodentpName" label="生产厂家" width="150" show-overflow-tooltip></el-table-column>

              <!-- 手工录入模态框内的操作列 -->
              <el-table-column label="操作" width="160" fixed="right" align="center">
                <template slot-scope="scope">
                  <div style="display: flex; align-items: center; justify-content: center; gap: 4px; flex-wrap: nowrap;">
                    <el-button
                      type="text"
                      size="mini"
                      @click="openTraceManage(scope.row)"
                      v-if="scope.row.isBar === 1"
                      style="padding: 0 4px; margin: 0; font-size: 12px; line-height: 1.2;"
                    >
                      追溯码（{{ scope.row.traceDetails.length }}）
                    </el-button>
                    <el-button
                      type="text"
                      size="mini"
                      class="delete-btn"
                      @click="handleRemoveManualItem(scope.row)"
                      style="padding: 0 4px; margin: 0; font-size: 12px; line-height: 1.2;"
                    >
                      删除
                    </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
            <!-- 空数据提示 -->
            <div v-if="manualTableItems.length === 0 && !manualTableLoading" class="empty-data">
              <el-empty description="请从上方选择药品添加到盘点表"></el-empty>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button size="mini" @click="manualInputDialogVisible = false">关闭</el-button>
        <el-button size="mini" type="primary" @click="handleManualDialogComplete" :loading="manualCompleteLoading"
          :disabled="manualTableItems.some(item => item.isBar === 1 && !item.isValid)">
          完成
        </el-button>
      </div>
    </el-dialog>

    <!-- 追溯码维护追加窗口 -->
    <el-dialog title="追溯码维护" :visible.sync="traceManageDialogVisible"
     width="52%" custom-class="trace-code-wh" append-to-body
     :close-on-click-modal="false">
      <div v-if="currentTraceRow">
        <!-- 追加区域 -->
        <div class="add-trace-area" style="margin-bottom: 15px;">
          <el-input size="mini" v-model="newTraceCode" placeholder="扫描或输入新追溯码" style="width: 300px;" @keyup.enter="addTraceCodeToBatch"
          clearable></el-input>
          <el-button type="primary" size="mini" @click="addTraceCodeToBatch">追加</el-button>
        </div>
        <el-table :data="batchTraceCodes" border row-key="id" max-height="400">
          <!-- 追溯码列（根据编辑状态切换显示） -->
          <el-table-column label="追溯码" min-width="188">
            <template slot-scope="scope">
              <el-input v-if="scope.row.editing" v-model="scope.row.code" size="mini" placeholder="输入追溯码" @keyup.enter.native="saveTraceCodeEdit(scope.row)"></el-input>
              <span v-else>{{ scope.row.code }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="pacCnt" label="包装数量" width="100" align="center">
            <template slot-scope="scope">{{ scope.row.pacCnt }}</template>
          </el-table-column>
          <el-table-column label="可用数量" width="100" align="center">
            <template slot-scope="scope">
              <el-input v-model="scope.row.availableQty" :min="0" size="mini" @change="handleAvailableQtyChange(scope.row, scope.$index)" controls-position="right"></el-input>
            </template>
          </el-table-column>
          <el-table-column prop="drugName" label="药品名称" width="160" show-overflow-tooltip></el-table-column>
          <el-table-column prop="entName" label="生产厂家" width="160" show-overflow-tooltip></el-table-column>
          <el-table-column label="操作" width="120" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="scope.row.editing ? saveTraceCodeEdit(scope.row) : (scope.row.editing = true)">
                {{ scope.row.editing ? '保存' : '修改追溯码' }}
              </el-button>
              <el-button type="text" size="mini" class="delete-btn" @click="removeTraceCode(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="traceManageDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" @click="confirmTraceManage" :disabled="hasEditing">确认汇总</el-button>
      </span>
    </el-dialog>

    <!-- 追溯码详情模态框 -->
    <el-dialog
      title="追溯码详情"
      :visible.sync="traceCodeDetailDialogVisible"
      width="70%"
      :close-on-click-modal="false"
      top="5vh"
      append-to-body
      custom-class="trace-code-detail-modal"
    >
      <div v-if="currentTraceDetailData" class="trace-code-detail-container">
        <el-descriptions :column="4" border size="small" class="trace-code-descriptions">
          <el-descriptions-item label="当前追溯码" :span="1">
            {{ currentTraceCode }}
          </el-descriptions-item>
          <el-descriptions-item label="药品名称" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.physicName || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="类型" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.physicTypeDesc || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="规格" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.pkgSpecCrit || '--' }}
          </el-descriptions-item>

          <el-descriptions-item label="批准文号" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.approvalLicenceNo || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="包装数量" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.prepnSpec || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="剂型" :span="1">
            {{ currentTraceDetailData.drugEntBaseDTO?.prepnTypeDesc || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="批次号" :span="1">
            {{ currentTraceDetailData.codeProduceInfoDTO?.produceInfoList?.[0]?.batchNo || '--' }}
          </el-descriptions-item>

          <el-descriptions-item label="生产厂商" :span="2">
            {{ currentTraceDetailData.pUserEntDTO?.entName || '--' }}
          </el-descriptions-item>
          <el-descriptions-item label="有效期" :span="2">
            {{ currentTraceDetailData.drugEntBaseDTO?.exprie || '--' }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <span slot="footer">
        <el-button size="mini" @click="traceCodeDetailDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" @click="confirmTraceCodeDetail">确认录入</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  stockPage,
  selectInformation,
  stockAdd,
  stockInfo,
  detailStock,
  infoIsStocked,
  getValueStock,
  stockEditDetail,
  stockInfoing,
  toStockExamine,
  deleteStock,
} from "@/api/PSI.js";
import { msfxCodeDetail } from "@/api/systemCatalogue/Western.js";

export default {
  name: 'DrugInventory',
  directives: {
    focus: {
      inserted(el) {
        const input = el.querySelector('input');
        if (input) {
          input.focus();
          input.select();
        } else if (el.tagName === 'INPUT') {
          el.focus();
          el.select();
        }
      }
    }
  },
  data() {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split('T')[0];
    return {
      currentStep: 1,
      queryForm: {
        stockType: 1,
        status: '',
        keyword: '',
        pageNum: 1,
        pageSize: 10
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      stockList: [],
      tableLoading: false,
      docNumber: '',
      filterType: 'all',
      inventoryItems: [],
      manualTableItems: [],
      createDialogVisible: false,
      createLoading: false,
      createForm: {
        docNum: '',
        bookDate: formattedDate,
        warehouseId: ''
      },
      manualInputDialogVisible: false,
      manualDrugSearchLoading: false,
      manualDrugOptions: [],
      manualTableLoading: false,
      manualCompleteLoading: false,
      manualSearchParams: {
        drugName: "",
        manuLotnum: ""
      },
      searchParams: {
        drugName: "",
        manuLotnum: "",
        pageNum: 1,
        pageSize: 10,
        stockType: 0,
      },
      allDrugOptions: [],
      filteredDrugOptions: [],
      isLoadingDrugs: false,
      isDrugDataLoaded: false,
      isWarehouseLocked: true,
      loading: false,
      currentDate: formattedDate,
      isExistingTask: false,
      total: 0,
      selectedRows: [],
      editingRows: new Set(),
      currentEditingCell: null,
      currentInventoryId: null,
      showTaskTables: false,
      taskTableLoading: false,
      taskTableItems: [],
      taskTableStockType: 1,
      warehouseOptions: [
        { id: 1, name: '药库' },
        { id: 2, name: '药房' },
        { id: 3, name: '卫材' }
      ],
      inventoryPersonOptions: [],
      completedItems: [],
      inventoryForm: {
        warehouseId: 1,
        inventoryPersonId: '',
        bookDate: formattedDate,
        remark: '',
        method: 'physical'
      },
      traceCodeInput: '',
      traceCodeLoading: false,
      traceManageDialogVisible: false,
      currentTraceRow: null,
      batchTraceCodes: [],
      currentDetail: '',
      selectedWarehouseType: 1,
      currentStatus: 0,
      editMode: false,
      editingCompletedItem: null,
      auditLoading: false,
      carryOverLoading: false,
      detailDialogVisible: false,
      reverseCarryOverLoading: false,
      traceCodeDetailDialogVisible: false,
      isEditMode: false,
      detailItems: [],
      currentTraceDetailData: null,
      currentTraceCode: '',
      detailLoading: false,
      createRules: {
        warehouseId: [{ required: true, message: '请选择盘点药库', trigger: 'change' }]
      },
      editingRowId: null,
      isEditManual: false,
      newTraceCode: '',
      currentUser: {
        name: '管理员',
        shortName: 'GLY'
      },
      // 药品类型映射常量
      drugTypeMap: {
        1: '西药',
        2: '中药',
        3: '卫材'
      },
      drugTypeTagMap: {
        1: 'primary',
        2: 'success',
        3: 'warning'
      }
    };
  },
  computed: {
    step2Title() {
      if (this.isEditMode) {
        const stockTypeMap = { 1: '药库', 2: '药房', 3: '卫材' };
        const warehouseName = stockTypeMap[this.selectedWarehouseType] || '药库';
        return `修改${warehouseName}盘点单据`;
      } else {
        const stockTypeMap = { 1: '药库', 2: '药房', 3: '卫材' };
        const warehouseName = stockTypeMap[this.createForm.warehouseId] || '药品';
        return `新建${warehouseName}盘点单据`;
      }
    },
    stepActive() {
      if (this.currentStep === 1 && this.currentStatus === 1) {
        return 2;
      }
      return this.currentStep - 1;
    },
    hasEditing() {
      return this.batchTraceCodes.some(t => t.editing === true);
    },
    filteredInventoryItems() {
      if (!this.inventoryItems || this.inventoryItems.length === 0) return [];
      if (this.filterType === 'all') return this.inventoryItems;
      else if (this.filterType === 'diff') {
        return this.inventoryItems.filter(item => {
          const profitPacCnt = Number(item.profitPacCnt) || 0;
          const profitMinCnt = Number(item.profitMinCnt) || 0;
          return profitPacCnt !== 0 || profitMinCnt !== 0;
        });
      }
      return this.inventoryItems;
    },
    filteredManualTableItems() {
      if (!this.manualTableItems || this.manualTableItems.length === 0) return [];
      if (this.filterType === 'all') return this.manualTableItems;
      else if (this.filterType === 'diff') {
        return this.manualTableItems.filter(item => {
          const profitPacCnt = Number(item.profitPacCnt) || 0;
          const profitMinCnt = Number(item.profitMinCnt) || 0;
          return profitPacCnt !== 0 || profitMinCnt !== 0;
        });
      }
      return this.manualTableItems;
    },
    diffDrugCount() {
      if (!this.inventoryItems || this.inventoryItems.length === 0) return 0;
      return this.inventoryItems.filter(item => {
        const profitPacCnt = Number(item.profitPacCnt) || 0;
        const profitMinCnt = Number(item.profitMinCnt) || 0;
        return profitPacCnt !== 0 || profitMinCnt !== 0;
      }).length;
    },
    manualDiffDrugCount() {
      if (!this.manualTableItems || this.manualTableItems.length === 0) return 0;
      return this.manualTableItems.filter(item => {
        const profitPacCnt = Number(item.profitPacCnt) || 0;
        const profitMinCnt = Number(item.profitMinCnt) || 0;
        return profitPacCnt !== 0 || profitMinCnt !== 0;
      }).length;
    },
    totalTablePurcSumAmt() {
      return this.stockList.reduce((sum, item) => sum + (Number(item.tablePurcSumAmt) || 0), 0);
    },
    totalTableSaleSumAmt() {
      return this.stockList.reduce((sum, item) => sum + (Number(item.tableSaleSumAmt) || 0), 0);
    },
    selectedWarehouseName() {
      const warehouse = this.warehouseOptions.find(item => item.id === this.inventoryForm.warehouseId);
      return warehouse ? warehouse.name : '未选择';
    },
    selectedInventoryPersonName() {
      const person = this.inventoryPersonOptions.find(item => item.id === this.inventoryForm.inventoryPersonId);
      return person ? person.fullName || person.name : '未选择';
    },
    stockTypeMapping() {
      return { 1: 1, 2: 2, 3: 3 };
    },
    totalProfitCount() {
      return this.inventoryItems.reduce((sum, item) => {
        const profit = (item.profitMinCnt || 0) + (item.profitPacCnt || 0);
        return profit > 0 ? sum + profit : sum;
      }, 0);
    },
    totalLossCount() {
      return this.inventoryItems.reduce((sum, item) => {
        const loss = (item.profitMinCnt || 0) + (item.profitPacCnt || 0);
        return loss < 0 ? sum + Math.abs(loss) : sum;
      }, 0);
    },
    totalProfitAmount() {
      return this.inventoryItems.reduce((sum, item) => sum + (item.profitPurcAmt || 0), 0);
    },
    totalStockAmount() {
      return this.completedItems.reduce((sum, item) => sum + (item.stockPurcAmt || 0), 0);
    },
    totalTableAmount() {
      return this.completedItems.reduce((sum, item) => sum + (item.tablePurcAmt || 0), 0);
    },
    auditButtonText() {
      const textMap = { 0: '审核', 1: '审核', 2: '待结转', 3: '已结转', 4: '已反结转' };
      return textMap[this.currentStatus] || '审核';
    },
    isAuditDisabled() {
      return this.currentStatus === 3;
    },
    showReverseButton() {
      return this.currentStatus === 3;
    }
  },
  watch: {
    'inventoryForm.warehouseId': {
      handler(newVal) {
        this.searchParams.stockType = this.stockTypeMapping[newVal];
      },
      immediate: true
    },
    showTaskTables(newVal) {
      if (newVal) {
        this.loadTaskTableData();
      }
    }
  },
  created() {
    this.loadStockList();
    this.loadInventoryPersons();
    this.loadCurrentUserInfo();
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleGlobalClick);
  },
  methods: {
    // ========== 类型映射工具方法 ==========
    // 药品类型文本（用于第二步表格和手工录入表格）
    getDrugTypeText(type) {
      return this.drugTypeMap[type] || '未知';
    },
    // 药品类型标签样式
    getDrugTypeTagType(type) {
      return this.drugTypeTagMap[type] || 'info';
    },
    // 盘点药库类型文本（用于第一步表格、详情表格）
    getStockTypeText(stockType) {
      const textMap = { 1: '药库', 2: '药房', 3: '卫材' };
      return textMap[stockType] || '未知';
    },
    // 盘点药库类型标签样式
    getStockTypeTagType(stockType) {
      const typeMap = { 1: 'primary', 2: 'success', 3: 'warning' };
      return typeMap[stockType] || 'info';
    },
    getStatusTagType(status) {
      const typeMap = { 0: 'info', 1: 'warning', 2: 'primary', 3: 'success', 4: 'danger' };
      return typeMap[status] || 'info';
    },
    getStatusText(status) {
      const statusNum = Number(status);
      const statusMap = { 0: '草稿', 1: '已提交', 2: '已审核', 3: '已结转', 4: '反结转' };
      return statusMap[statusNum] || `未知状态(${status})`;
    },
    getProfitLossClass(value) {
      if (value > 0) return 'profit-text';
      if (value < 0) return 'loss-text';
      return '';
    },
    // ========== 原有方法 ==========
    handleGlobalClick(event) {
      const isInTable = event.target.closest('.el-table__body-wrapper') !== null;
      const isInput = event.target.tagName === 'INPUT';
      if (!isInTable && !isInput) {
        this.closeAllTableEditing();
      }
    },
    async loadStockList() {
      try {
        this.tableLoading = true;
        const params = {
          stockType: this.queryForm.stockType,
          status: this.queryForm.status,
          keyword: this.queryForm.keyword,
          pageNum: this.pagination.currentPage,
          pageSize: this.pagination.pageSize
        };
        const res = await stockPage(params);
        if (res.code === 200) {
          this.stockList = res.data.content || res.data.list || [];
          this.pagination.total = res.data.totalSize || res.data.total || 0;
        } else {
          this.$message.error(res.message || '获取盘点任务列表失败');
          this.stockList = [];
          this.pagination.total = 0;
        }
      } catch (error) {
        console.error('加载盘点任务列表失败:', error);
        this.$message.error('加载盘点任务列表失败');
        this.stockList = [];
        this.pagination.total = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    handleFilterChange() {
      this.pagination.currentPage = 1;
      this.loadStockList();
    },
    handleSearch() {
      this.pagination.currentPage = 1;
      this.loadStockList();
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.currentPage = 1;
      this.loadStockList();
    },
    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.loadStockList();
    },
    handleCreateTask() {
      this.createForm = {
        docNum: '',
        bookDate: this.currentDate,
        warehouseId: ''
      };
      this.createDialogVisible = true;
      this.isEditMode = false;
      this.$nextTick(() => {
        this.generateDocNumber();
      });
      if (this.$refs.createForm) {
        this.$refs.createForm.clearValidate();
      }
    },
    async handleCreateConfirm() {
      this.$refs.createForm.validate(async (valid) => {
        if (valid) {
          try {
            this.createLoading = true;
            if (!this.createForm.docNum) {
              this.generateDocNumber();
            }
            const params = {
              docNum: this.createForm.docNum,
              stockDate: this.createForm.bookDate,
              stockType: this.createForm.warehouseId,
              remark: '新建盘点任务'
            };
            const res = await stockAdd(params);
            if (res.code === 200) {
              this.$message.success('盘点任务创建成功');
              this.createDialogVisible = false;
              if (res.data) {
                if (res.data.id) {
                  this.currentInventoryId = res.data.id;
                } else if (typeof res.data === 'string' || typeof res.data === 'number') {
                  this.currentInventoryId = res.data;
                }
              }
              if (!this.currentInventoryId && res.data) {
                const possibleIdFields = ['inventoryId', 'stockId', 'taskId', 'docId'];
                for (const field of possibleIdFields) {
                  if (res.data[field]) {
                    this.currentInventoryId = res.data[field];
                    break;
                  }
                }
              }
              if (!this.currentInventoryId) {
                console.warn('创建盘点任务成功，但未返回有效的任务ID');
                await this.tryGetLatestInventoryId();
              }
              console.log('创建任务成功，当前盘点任务ID:', this.currentInventoryId);
              this.currentStep = 2;
              this.currentStatus = 0;
              this.isEditMode = false;
              this.selectedWarehouseType = this.createForm.warehouseId;
              this.searchParams.stockType = this.createForm.warehouseId;
              this.$message.success('已进入盘点执行阶段');
              this.$nextTick(() => {
                setTimeout(() => {
                  this.loadStep2Data();
                }, 100);
              });
              this.loadStockList();
            } else if (res.code === 201) {
              this.handleExistingTask(res.message, res.code);
            } else {
              this.$message.error(res.message || '创建盘点任务失败');
            }
          } catch (error) {
            console.error('创建盘点任务失败:', error);
            this.$message.error('创建盘点任务失败');
          } finally {
            this.createLoading = false;
          }
        }
      });
    },
    async tryGetLatestInventoryId() {
      try {
        console.log('尝试获取最新的盘点任务ID');
        const params = {
          stockType: this.createForm.warehouseId,
          pageNum: 1,
          pageSize: 5,
          sortField: 'createTime',
          sortOrder: 'desc'
        };
        const res = await stockPage(params);
        if (res.code === 200 && res.data && res.data.content && res.data.content.length > 0) {
          const latestTask = res.data.content[0];
          this.currentInventoryId = latestTask.id;
          console.log('获取到最新盘点任务ID:', this.currentInventoryId);
        } else {
          console.warn('无法获取最新盘点任务ID');
        }
      } catch (error) {
        console.error('获取最新盘点任务ID失败:', error);
      }
    },
    handleExistingTask(message, code = null) {
      // 原逻辑已存在，此处保留
    },
    loadExistingDraftTask() {
      // 原逻辑已存在
    },
    async loadStep2Data() {
      try {
        this.tableLoading = true;
        const params = {
          stockType: this.selectedWarehouseType,
          pageNum: this.searchParams.pageNum,
          pageSize: this.searchParams.pageSize
        };
        if (this.currentInventoryId) {
          params.id = this.currentInventoryId;
          params.inventoryId = this.currentInventoryId;
          params.stockId = this.currentInventoryId;
        }
        console.log('第二步数据查询参数:', params);
        const res = await infoIsStocked(params);
        if (res.code === 200) {
          const responseData = res.data;
          let items = [];
          let total = 0;
          if (Array.isArray(responseData)) {
            items = responseData;
            total = items.length;
          } else if (responseData.content) {
            items = responseData.content;
            total = responseData.totalSize || responseData.total || items.length;
          } else if (responseData.list) {
            items = responseData.list;
            total = responseData.totalSize || responseData.total || items.length;
          } else {
            items = [];
            total = 0;
          }
          this.inventoryItems = items;
          this.total = total;
          console.log('第二步数据加载成功，记录数:', this.inventoryItems.length);

          this.inventoryItems.forEach((item) => {
            item.isBar = item.isBar || 0;
            item.pacCnt = Number(item.pacCnt) || 1;
            let traceDetails = [];
            if (item.tracCodeJson && typeof item.tracCodeJson === 'string' && item.tracCodeJson.length > 0) {
              try {
                const parsed = JSON.parse(item.tracCodeJson);
                if (Array.isArray(parsed)) {
                  traceDetails = parsed.map(t => ({
                    code: t.barcode,
                    availableQty: t.chgCnt || 0,
                    drugName: item.drugName || '',
                    prepnSpec: '',
                    approvalLicenceNo: '',
                    entName: item.prodentpName || ''
                  }));
                }
              } catch(e) {
                console.warn('解析 tracCodeJson 失败:', e);
              }
            }
            item.traceDetails = traceDetails;
            item.tableMinCnt = Number(item.tableMinCnt) || 0;
            item.tablePacCnt = Number(item.tablePacCnt) || 0;
            if (!item.traceDetails) {
              this.$set(item, 'traceDetails', []);
            }
            this.$set(item, 'originalMinCnt', item.tableMinCnt);
            this.$set(item, 'originalPacCnt', item.tablePacCnt);
            this.$set(item, 'editingPac', false);
            this.$set(item, 'editingMin', false);
          });
          this.closeAllTableEditing();
        } else {
          this.$message.error(res.message || '加载数据失败');
          this.inventoryItems = [];
          this.total = 0;
        }
      } catch (error) {
        console.error('加载第二步数据失败:', error);
        this.$message.error('加载数据失败');
        this.inventoryItems = [];
        this.total = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    syncManualTableItems() {
      if (this.manualInputDialogVisible && this.manualTableItems.length > 0) {
        this.manualTableItems = this.manualTableItems.map(oldItem => {
          const newItem = this.inventoryItems.find(
            item => item.drugId === oldItem.drugId && item.manuLotnum === oldItem.manuLotnum
          );
          if (newItem) {
            const merged = {
              ...newItem,
              isManual: true,
              editingPac: false,
              editingMin: false,
              traceDetails: newItem.traceDetails || []
            };
            this.validateTraceCount(merged);
            return merged;
          }
          return oldItem;
        });
        this.$forceUpdate();
      }
    },
    handleTableCellDblClick(row, type) {
      if (!row.hasOwnProperty('editingPac')) {
        this.$set(row, 'editingPac', false);
      }
      if (!row.hasOwnProperty('editingMin')) {
        this.$set(row, 'editingMin', false);
      }
      this.closeAllTableEditing();
      if (type === 'pac') {
        this.$set(row, 'editingPac', true);
        this.$set(row, 'editingMin', false);
      } else if (type === 'min') {
        this.$set(row, 'editingPac', false);
        this.$set(row, 'editingMin', true);
      }
      this.$forceUpdate();
    },
    closeAllTableEditing() {
      this.inventoryItems.forEach(row => {
        this.$set(row, 'editingPac', false);
        this.$set(row, 'editingMin', false);
      });
      this.editingRows.clear();
      this.currentEditingCell = null;
    },
    finishTableEditing(row, type) {
      if (type === 'pac') {
        this.$set(row, 'editingPac', false);
      } else if (type === 'min') {
        this.$set(row, 'editingMin', false);
      }
      this.saveTableItemChange(row, type);
      this.editingRows.delete(row.id || row.drugId);
      this.currentEditingCell = null;
    },
    handleTableQuantityBlur(row, type) {
      setTimeout(() => {
        if ((type === 'pac' && row.editingPac) || (type === 'min' && row.editingMin)) {
          this.finishTableEditing(row, type);
        }
      }, 200);
    },
    calculateTableProfitLoss(row) {
      const stockPacCnt = Number(row.stockPacCnt) || 0;
      const stockMinCnt = Number(row.stockMinCnt) || 0;
      const tablePacCnt = Number(row.tablePacCnt) || 0;
      const tableMinCnt = Number(row.tableMinCnt) || 0;
      const minPurcPric = Number(row.minPurcPric) || 0;
      const maxPurcPric = Number(row.maxPurcPric) || 0;
      const minSalePric = Number(row.minSalePric) || 0;
      const maxSalePric = Number(row.maxSalePric) || 0;

      row.profitPacCnt = stockPacCnt - tablePacCnt;
      row.profitMinCnt = stockMinCnt - tableMinCnt;
      row.profitPurcAmt = row.profitMinCnt * minPurcPric + row.profitPacCnt * maxPurcPric;
      row.profitSaleAmt = row.profitMinCnt * minSalePric + row.profitPacCnt * maxSalePric;
      row.tablePurcAmt = tableMinCnt * minPurcPric + tablePacCnt * maxPurcPric;
      row.tableSaleAmt = tableMinCnt * minSalePric + tablePacCnt * maxSalePric;
      row.stockPurcAmt = stockMinCnt * minPurcPric + stockPacCnt * maxPurcPric;
      row.stockSaleAmt = stockMinCnt * minSalePric + stockPacCnt * maxSalePric;

      row.profitPurcAmt = Number(row.profitPurcAmt.toFixed(2));
      row.profitSaleAmt = Number(row.profitSaleAmt.toFixed(2));
      row.tablePurcAmt = Number(row.tablePurcAmt.toFixed(2));
      row.tableSaleAmt = Number(row.tableSaleAmt.toFixed(2));
      row.stockPurcAmt = Number(row.stockPurcAmt.toFixed(2));
      row.stockSaleAmt = Number(row.stockSaleAmt.toFixed(2));
      this.$forceUpdate();
    },
    async saveTableItemChange(row, type) {
      try {
        const params = {
          id: row.id,
          stockId: this.currentInventoryId,
          drugId: row.drugId,
          drugCode: row.drugCode,
          stockMinCnt: Number(row.tableMinCnt) || 0,
          stockPacCnt: Number(row.tablePacCnt) || 0,
          stockType: this.selectedWarehouseType,
          tracCodeJson: this.buildTracCodeJson(row)
        };
        console.log('保存表格数量修改:', params);
        const res = await stockEditDetail(params);
        if (res.code === 200) {
          this.$message.success('盘点数量更新成功');
          row.originalMinCnt = row.tableMinCnt;
          row.originalPacCnt = row.tablePacCnt;
          await this.loadStep2Data();
          this.$forceUpdate();
        } else {
          this.$message.error(res.message || '更新盘点数量失败');
          this.restoreTableOriginalValues(row, type);
        }
      } catch (error) {
        console.error('更新表格盘点数量失败:', error);
        this.$message.error('更新盘点数量失败，请重试');
        this.restoreTableOriginalValues(row, type);
      }
    },
    restoreTableOriginalValues(row, type) {
      if (type === 'min') {
        row.tableMinCnt = row.originalMinCnt;
      } else if (type === 'pac') {
        row.tablePacCnt = row.originalPacCnt;
      }
      this.calculateTableProfitLoss(row);
    },
    calculateTableTotals() {
      console.log('重新计算表格总计');
    },
    handleManualInput() {
      this.manualInputDialogVisible = true;
      this.manualTableItems = [];
      this.manualSearchParams.drugName = "";
      this.$nextTick(() => {
        this.loadManualDrugData();
      });
    },
    async loadManualDrugData() {
      try {
        this.manualDrugSearchLoading = true;
        console.log('加载手工录入药品数据');
        const params = {
          msg: '',
          stockType: this.selectedWarehouseType
        };
        const res = await getValueStock(params);
        if (res.code === 200) {
          const data = res.data || [];
          console.log('手工录入药品数据加载成功:', data.length, '条记录');
          this.processManualDrugOptions(data);
        } else {
          console.error('加载手工录入药品数据失败:', res.message);
          this.$message.error(res.message || '加载药品数据失败');
          this.manualDrugOptions = [];
        }
      } catch (error) {
        console.error('加载手工录入药品数据失败:', error);
        this.$message.error('加载药品数据失败');
        this.manualDrugOptions = [];
      } finally {
        this.manualDrugSearchLoading = false;
      }
    },
    processManualDrugOptions(data) {
      if (!data || data.length === 0) {
        this.manualDrugOptions = [];
        return;
      }
      const grouped = {};
      data.forEach(item => {
        const drugName = item.drugName || item.name || '未知药品';
        if (!grouped[drugName]) {
          grouped[drugName] = [];
        }
        const drugItem = {
          drugId: item.drugId || item.id,
          drugName: drugName,
          drugSpec: item.drugSpec || item.spec || '',
          manuLotnum: item.manuLotnum || item.batchNumber || '',
          prodentpName: item.prodentpName || item.manufacturer || '',
          drugType: item.drugType || 0,
          ...item
        };
        grouped[drugName].push(drugItem);
      });
      this.manualDrugOptions = Object.keys(grouped).map(drugName => ({
        label: drugName,
        options: grouped[drugName]
      }));
      console.log('手工录入药品选项处理完成:', this.manualDrugOptions.length, '个分组');
    },
    handleManualDialogComplete() {
      if (this.manualTableItems.length === 0) {
        this.$message.warning('请先添加药品到盘点表');
        return;
      }
      const invalidRows = this.manualTableItems.filter(item => item.isBar === 1 && !item.isValid);
      if (invalidRows.length > 0) {
        this.$message.warning('存在盘点数量与追溯码数量不一致的行，请修正后再完成');
        return;
      }
      try {
        let addedCount = 0;
        let updatedCount = 0;
        this.manualTableItems.forEach(manualItem => {
          const existingIndex = this.inventoryItems.findIndex(
            item => item.drugId === manualItem.drugId && item.manuLotnum === manualItem.manuLotnum
          );
          if (existingIndex !== -1) {
            this.inventoryItems[existingIndex] = {
              ...this.inventoryItems[existingIndex],
              ...manualItem
            };
            updatedCount++;
          } else {
            this.inventoryItems.push({
              ...manualItem,
              id: manualItem.id || `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            });
            addedCount++;
          }
        });
        let message = '';
        if (addedCount > 0 && updatedCount > 0) {
          message = `成功添加 ${addedCount} 条新数据，更新 ${updatedCount} 条现有数据`;
        } else if (addedCount > 0) {
          message = `成功添加 ${addedCount} 条药品数据`;
        } else {
          message = `成功更新 ${updatedCount} 条药品数据`;
        }
        this.$message.success(message);
        this.manualInputDialogVisible = false;
        this.manualTableItems = [];
        this.$forceUpdate();
      } catch (error) {
        console.error('完成手工录入失败:', error);
        this.$message.error('操作失败，请重试');
      }
    },
    async handleManualDrugSearch(query) {
      try {
        this.manualDrugSearchLoading = true;
        if (!query || query.trim() === '') {
          if (this.manualDrugOptions.length > 0) {
            return;
          }
          await this.loadManualDrugData();
          return;
        }
        console.log('手工录入搜索药品:', query);
        const params = {
          msg: query,
          stockType: this.selectedWarehouseType
        };
        const res = await getValueStock(params);
        if (res.code === 200) {
          const data = res.data || [];
          console.log('手工录入搜索返回数据:', data.length, '条');
          this.processManualDrugOptions(data);
        } else {
          this.$message.error(res.message || '搜索药品失败');
          this.manualDrugOptions = [];
        }
      } catch (error) {
        console.error('手工录入搜索药品失败:', error);
        this.$message.error('搜索药品失败');
        this.manualDrugOptions = [];
      } finally {
        this.manualDrugSearchLoading = false;
      }
    },
    handleManualSelectVisible(visible) {
      console.log('手工录入下拉框显示状态:', visible);
      if (visible && this.manualDrugOptions.length === 0) {
        this.loadManualDrugData();
      }
    },
    handleManualDrugChange(value) {
      console.log('手工录入药品选择变化:', value);
    },
    handleManualKeyPress(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleAddToManualTable();
      }
    },
    handleAddToManualTable() {
      if (!this.manualSearchParams.drugName) {
        this.$message.warning('请先选择药品');
        return;
      }
      let selectedDrug = null;
      for (const group of this.manualDrugOptions) {
        selectedDrug = group.options.find(drug => drug.drugName === this.manualSearchParams.drugName);
        if (selectedDrug) break;
      }
      if (!selectedDrug) {
        this.$message.warning('未找到对应的药品信息');
        return;
      }
      const exists = this.manualTableItems.some(item =>
        item.drugId === selectedDrug.drugId && item.manuLotnum === selectedDrug.manuLotnum
      );
      if (exists) {
        this.$message.warning('该药品已存在于盘点表中');
        return;
      }
      this.loadManualDrugDetail(selectedDrug);
    },
    async saveTraceCodeToServer(row) {
      if (!row || !row.id) {
        console.warn('无效的行数据，无法保存追溯码');
        return;
      }
      const params = {
        id: row.id,
        stockId: this.currentInventoryId,
        drugId: row.drugId,
        drugCode: row.drugCode,
        stockMinCnt: Number(row.tableMinCnt) || 0,
        stockPacCnt: Number(row.tablePacCnt) || 0,
        stockType: this.selectedWarehouseType,
        tracCodeJson: this.buildTracCodeJson(row)
      };
      try {
        const res = await stockEditDetail(params);
        if (res.code === 200) {
          this.$message.success('追溯码保存成功');
        } else {
          this.$message.error(res.message || '保存追溯码失败');
        }
      } catch (error) {
        console.error('保存追溯码异常:', error);
        this.$message.error('保存追溯码异常');
      }
    },
    async loadManualDrugDetail(drugInfo) {
      try {
        this.manualTableLoading = true;
        const params = {
          stockType: this.selectedWarehouseType,
          drugName: drugInfo.drugName,
          manuLotnum: drugInfo.manuLotnum,
          prodentpName: drugInfo.prodentpName
        };
        if (this.currentInventoryId) {
          params.id = this.currentInventoryId;
        }
        const res = await stockInfo(params);
        if (res.code === 200) {
          const data = res.data || [];
          if (data.length > 0) {
            const drugDetail = data[0];
            // 修复：确保 drugType 字段被正确传递，优先使用接口返回的值，否则从 drugInfo 获取
            const drugType = drugDetail.drugType !== undefined && drugDetail.drugType !== null
              ? drugDetail.drugType
              : (drugInfo.drugType || 0);
			const tableMin = Number(drugDetail.tableMinCnt) !== undefined ? Number(drugDetail.tableMinCnt) : Number(drugDetail.stockMinCnt) || 0;
			const tablePac = Number(drugDetail.tablePacCnt) !== undefined ? Number(drugDetail.tablePacCnt) : Number(drugDetail.stockPacCnt) || 0;
            const newItem = {
              id: drugDetail.id || `manual_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
              drugId: drugDetail.drugId,
              drugCode: drugDetail.drugCode,
              drugName: drugDetail.drugName,
              drugType: drugType,
              pacCnt: Number(drugDetail.pacCnt) || 1,
              drugSpec: drugDetail.drugSpec,
              manuLotnum: drugDetail.manuLotnum,
              prodentpName: drugDetail.prodentpName,
              minUnt: drugDetail.minUnt,
              pacUnt: drugDetail.pacUnt,
              isBar: drugDetail.isBar || 0,
              isValid: true,
              totalTraceQty: 0,
              traceDetails: [],
              isManual: true,
			  
			  stockMinCnt: tableMin,   // 盘点数量初始等于账面数量
			  stockPacCnt: tablePac,
			  tableMinCnt: tableMin,   // ✅ 正确使用账面数量
			  tablePacCnt: tablePac,
              minPurcPric: Number(drugDetail.minPurcPric) || 0,
              maxPurcPric: Number(drugDetail.maxPurcPric) || 0,
              minSalePric: Number(drugDetail.minSalePric) || 0,
              maxSalePric: Number(drugDetail.maxSalePric) || 0,
              originalMinCnt: Number(drugDetail.stockMinCnt) || 0,
              originalPacCnt: Number(drugDetail.stockPacCnt) || 0
            };
            this.validateTraceCount(newItem);
            this.manualTableItems.push(newItem);
            this.$message.success('药品已添加到盘点表');
            this.manualSearchParams.drugName = "";
          } else {
            this.$message.warning('未找到该药品的详细信息');
          }
        } else {
          this.$message.error(res.message || '获取药品详细信息失败');
        }
      } catch (error) {
        console.error('获取药品详细信息失败:', error);
        this.$message.error('获取药品详细信息失败');
      } finally {
        this.manualTableLoading = false;
      }
    },
    validateTraceCount(item) {
      if (item.isBar !== 1) {
        item.isValid = true;
        return true;
      }
      const pacCnt = Number(item.pacCnt) || 1;
      const expectedTraceCount = Number(item.stockPacCnt) + (Number(item.stockMinCnt) > 0 ? 1 : 0);
      const actualTraceCount = (item.traceDetails || []).length;
      const isValid = expectedTraceCount === actualTraceCount;
      item.isValid = isValid;
      return isValid;
    },
    calculateManualProfitLoss(item) {
      console.log('计算手工录入盈亏:', item.drugName);
      item.profitPacCnt = (Number(item.stockPacCnt) || 0) - (Number(item.tablePacCnt) || 0);
      item.profitMinCnt = (Number(item.stockMinCnt) || 0) - (Number(item.tableMinCnt) || 0);
      item.profitPurcAmt = item.profitMinCnt * (Number(item.minPurcPric) || 0) +
        item.profitPacCnt * (Number(item.maxPurcPric) || 0);
      item.profitSaleAmt = item.profitMinCnt * (Number(item.minSalePric) || 0) +
        item.profitPacCnt * (Number(item.maxSalePric) || 0);
      item.tablePurcAmt = (Number(item.tableMinCnt) || 0) * (Number(item.minPurcPric) || 0) +
        (Number(item.tablePacCnt) || 0) * (Number(item.maxPurcPric) || 0);
      item.tableSaleAmt = (Number(item.tableMinCnt) || 0) * (Number(item.minSalePric) || 0) +
        (Number(item.tablePacCnt) || 0) * (Number(item.maxSalePric) || 0);
      item.stockPurcAmt = (Number(item.stockMinCnt) || 0) * (Number(item.minPurcPric) || 0) +
        (Number(item.stockPacCnt) || 0) * (Number(item.maxPurcPric) || 0);
      item.stockSaleAmt = (Number(item.stockMinCnt) || 0) * (Number(item.minSalePric) || 0) +
        (Number(item.stockPacCnt) || 0) * (Number(item.maxSalePric) || 0);
      item.profitPurcAmt = Number(item.profitPurcAmt);
      item.profitSaleAmt = Number(item.profitSaleAmt);
      item.tablePurcAmt = Number(item.tablePurcAmt);
      item.tableSaleAmt = Number(item.tableSaleAmt);
      item.stockPurcAmt = Number(item.stockPurcAmt);
      item.stockSaleAmt = Number(item.stockSaleAmt);
      this.$forceUpdate();
    },
    handlePacChangeForTrace(item) {
      const pacCnt = Number(item.pacCnt) || 1;
      let inputQty = Number(item.stockPacCnt) || 0;
      if (inputQty < 0) inputQty = 0;
      const packQty = Math.floor(inputQty / pacCnt);
      const minQty = inputQty % pacCnt;
      item.stockPacCnt = packQty;
      item.stockMinCnt = minQty;
      const actualTotal = packQty * pacCnt + minQty;
      const traceTotal = item.traceDetails.reduce((sum, t) => sum + (Number(t.availableQty) || 0), 0);
      if (traceTotal !== actualTotal) {
        item.isValid = false;
        this.$message.warning(
          `当前盘点的包装单位数量（${packQty}）- 最小单位数量（${minQty}）与追溯码数量（${traceTotal}）不一致！请先去录入正确的追溯码数量。`
        );
      } else {
        item.isValid = true;
      }
      this.$forceUpdate();
    },
    handleManualQuantityBlur(item, type) {
      console.log('手工录入数量输入框失去焦点:', item.drugName, type);
      if (item.isBar === 1) {
        if (type === 'pac') {
          this.handlePacChange(item);
        } else if (type === 'min') {
          this.handleMinChange(item);
        }
        this.validateTraceCount(item);
        if (!item.isValid) {
          this.$alert(
            `当前盘点的包装单位数量（${item.stockPacCnt}）- 最小单位数量（${item.stockMinCnt}）与追溯码数量（${(item.traceDetails || []).length}）不一致！请先去录入正确的追溯码数量。`,
            '提示',
            { confirmButtonText: '我知道了', type: 'warning' }
          );
          return;
        }
      }
      this.saveManualItemChange(item, type);
    },
    handlePacChange(item) {
      let pac = Number(item.stockPacCnt) || 0;
      if (pac < 0) pac = 0;
      item.stockPacCnt = pac;
      this.$forceUpdate();
    },
    handleMinChange(item) {
      const pacCnt = Number(item.pacCnt) || 1;
      let inputMin = Number(item.stockMinCnt) || 0;
      if (inputMin < 0) inputMin = 0;
      const packQty = Math.floor(inputMin / pacCnt);
      const minQty = inputMin % pacCnt;
      item.stockPacCnt = packQty;
      item.stockMinCnt = minQty;
      this.$forceUpdate();
    },
    async saveManualItemChange(item, type) {
      if (item.isBar === 1 && !item.isValid) {
        this.$message.warning('数据不一致，请先修正追溯码数量');
        return;
      }
      try {
        const params = {
          id: item.id,
          stockId: this.currentInventoryId,
          drugId: item.drugId,
          drugCode: item.drugCode,
          stockMinCnt: Number(item.stockMinCnt) || 0,
          stockPacCnt: Number(item.stockPacCnt) || 0,
          stockType: this.selectedWarehouseType,
          tracCodeJson: this.buildTracCodeJson(item)
        };
        console.log('保存手工录入修改:', params);
        const res = await stockEditDetail(params);
        if (res.code === 200) {
          this.$message.success('盘点数量更新成功');
          await this.loadStep2Data();
          this.syncManualTableItems();
          this.$forceUpdate();
        } else {
          this.$message.error(res.message || '更新盘点数量失败');
        }
      } catch (error) {
        console.error('更新盘点数量失败:', error);
        this.$message.error('更新盘点数量失败，请重试');
      }
    },
    handleRemoveManualItem(item) {
      this.$confirm('确定要从盘点表中删除此药品吗？', '提示', {
        type: 'warning'
      }).then(() => {
        const index = this.manualTableItems.findIndex(i => i.id === item.id);
        if (index !== -1) {
          this.manualTableItems.splice(index, 1);
          this.$message.success('删除成功');
        }
      }).catch(() => {
        this.$message.info('已取消删除');
      });
    },
    handleManualComplete() {
      // 原逻辑
    },
    async saveManualItemToServer(item) {
      try {
        const params = {
          id: this.currentInventoryId,
          drugId: item.drugId,
          drugCode: item.drugCode,
          tableMinCnt: Number(item.tableMinCnt) || 0,
          tablePacCnt: Number(item.tablePacCnt) || 0,
          ...item
        };
        const res = await stockEditDetail(params);
        return res;
      } catch (error) {
        console.error('保存手工录入项失败:', error);
        throw error;
      }
    },
    generateDocNumber() {
      if (!this.createForm.warehouseId || !this.createForm.bookDate) {
        return;
      }
      const prefixMap = { 1: 'YK', 2: 'YF', 3: 'WC' };
      const prefix = prefixMap[this.createForm.warehouseId] || 'PD';
      let dateStr = '';
      if (this.createForm.bookDate) {
        dateStr = this.createForm.bookDate.replace(/-/g, '');
      } else {
        const now = new Date();
        dateStr = now.getFullYear() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0');
      }
      const timestamp = String(Date.now()).slice(-6);
      this.createForm.docNum = `${prefix}${dateStr}${timestamp}`;
    },
    handleWarehouseChange() {
      this.generateDocNumber();
    },
    loadCurrentUserInfo() {
      try {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
          const user = JSON.parse(userInfo);
          this.currentUser = {
            name: user.staffName || '管理员',
            shortName: this.getShortName(user.staffName) || 'GLY'
          };
        }
      } catch (error) {
        console.warn('获取用户信息失败，使用默认用户信息');
        this.currentUser = { name: '管理员', shortName: 'GLY' };
      }
    },
    getShortName(fullName) {
      if (!fullName) return 'USER';
      if (/[\u4e00-\u9fa5]/.test(fullName)) {
        return fullName.length >= 2 ? fullName.slice(0, 2) : fullName;
      }
      const words = fullName.split(' ');
      if (words.length > 1) {
        return words.map(word => word.charAt(0).toUpperCase()).join('');
      }
      return fullName.slice(0, 3).toUpperCase();
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      const number = Number(num);
      return Number.isInteger(number) ? number.toLocaleString() : number.toFixed(2);
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '¥0.00';
      return '¥' + Number(amount).toFixed(2);
    },
    formatCurrencyWithComma(amount) {
      if (amount === null || amount === undefined) return '¥0.00';
      const number = Number(amount);
      if (isNaN(number)) return '¥0.00';
      const parts = number.toFixed(2).split('.');
      const integerPart = parseInt(parts[0]).toLocaleString();
      const decimalPart = parts[1];
      return `¥${integerPart}.${decimalPart}`;
    },
    formatDateTime(dateString) {
      if (!dateString) return '--';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    },
    async loadInventoryPersons() {
      try {
        const res = await selectInformation();
        if (res.code === 200) {
          this.inventoryPersonOptions = res.data || [];
        } else {
          this.inventoryPersonOptions = [
            { id: 1, fullName: '张三' },
            { id: 2, fullName: '李四' },
            { id: 3, fullName: '王五' },
            { id: 4, fullName: '赵六' }
          ];
        }
      } catch (error) {
        console.error('加载盘点人数据失败:', error);
        this.inventoryPersonOptions = [
          { id: 1, fullName: '张三' },
          { id: 2, fullName: '李四' },
          { id: 3, fullName: '王五' },
          { id: 4, fullName: '赵六' }
        ];
      }
    },
    getBatchOptionLabel(item) {
      if (item.manuLotnum) {
        return `${item.drugName} - 批次: ${item.manuLotnum}`;
      } else {
        return `${item.drugName} - 无批次号`;
      }
    },
    handleManualDrugFocus() {
      console.log('手工录入下拉框获得焦点');
      if (this.manualDrugSearchLoading) {
        return;
      }
      if (this.manualDrugOptions.length === 0) {
        this.loadManualDrugData();
      }
    },
    calculateProfitLoss(item) {
      this.calculateManualProfitLoss(item);
    },
    async handleAuditSubmit() {
      try {
        await this.$confirm('确定要审核通过此盘点任务吗？审核通过后将无法修改', '提示', {
          type: 'warning'
        });
        const res = await toStockExamine({
          id: this.currentInventoryId,
          status: 2,
          stockType: this.selectedWarehouseType
        });
        console.log('审核接口响应:', res);
        if (res.code === 200) {
          this.$message.success('审核通过');
          this.loadStockList();
          this.$message.info('审核完成，可返回第一步查看状态');
        } else {
          const errorMessage = res.message || '审核失败';
          this.$message.error(`审核失败: ${errorMessage}`);
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('审核失败:', error);
          this.$message.error('审核失败');
        }
      }
    },
    async handleComplete() {
      if (!this.currentInventoryId) {
        this.$message.error('未找到当前盘点任务ID，请返回第一步重新选择任务');
        console.error('完成盘点失败：currentInventoryId 为空');
        return;
      }
      console.log('开始完成盘点，任务ID:', this.currentInventoryId);
      this.$confirm('确定要完成盘点吗？完成后将进入审核阶段。', '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          const requestParams = {
            id: this.currentInventoryId,
            status: 1,
            stockType: this.selectedWarehouseType
          };
          console.log('调用完成盘点接口，参数:', requestParams);
          const res = await toStockExamine(requestParams);
          console.log('完成盘点接口响应:', res);
          if (res.code === 200) {
            this.$message.success('盘点完成，已提交审核');
            this.currentStep = 1;
            this.currentStatus = 1;
            await this.loadStockList();
            this.$message.info('已返回盘点任务列表，可查看已提交的任务');
          } else {
            const errorMessage = res.message || '提交审核失败';
            this.$message.error(`提交失败: ${errorMessage}`);
          }
        } catch (error) {
          console.error('完成盘点失败:', error);
          this.$message.error('完成盘点失败，请重试');
          this.$message.info('网络错误，请检查连接后重试');
        }
      }).catch(() => {
        this.$message.info('已取消完成盘点操作');
      });
    },
    async loadCompletedData() {
      try {
        if (!this.currentInventoryId) {
          this.$message.warning('未找到当前盘点任务ID');
          this.completedItems = [];
          return;
        }
        this.tableLoading = true;
        const params = {
          id: this.currentInventoryId,
          stockType: this.selectedWarehouseType
        };
        const res = await stockInfo(params);
        if (res.code === 200) {
          this.completedItems = res.data || [];
          this.calculateCompletedSummary();
          if (this.completedItems.length > 0 && this.completedItems[0].docNum) {
            this.docNumber = this.completedItems[0].docNum;
          }
          this.$message.success(`已加载 ${this.completedItems.length} 条盘点数据`);
        } else {
          this.$message.error(res.message || '加载盘点数据失败');
          this.completedItems = [];
        }
      } catch (error) {
        console.error('加载盘点数据失败:', error);
        this.$message.error('加载盘点数据失败');
        this.completedItems = [];
      } finally {
        this.tableLoading = false;
      }
    },
    calculateCompletedSummary() {
      this.totalProfitCount = 0;
      this.totalLossCount = 0;
      this.totalProfitAmount = 0;
      this.totalStockAmount = 0;
      this.totalTableAmount = 0;
      this.completedItems.forEach(item => {
        item.profitMinCnt = Number(item.profitMinCnt) || 0;
        item.profitPurcAmt = Number(item.profitPurcAmt) || 0;
        item.stockMinCnt = Number(item.stockMinCnt) || 0;
        item.tableMinCnt = Number(item.tableMinCnt) || 0;
        item.stockPurcAmt = Number(item.stockPurcAmt) || 0;
        item.tablePurcAmt = Number(item.tablePurcAmt) || 0;
        if (item.profitMinCnt > 0) {
          this.totalProfitCount += item.profitMinCnt;
        } else if (item.profitMinCnt < 0) {
          this.totalLossCount += Math.abs(item.profitMinCnt);
        }
        this.totalProfitAmount += item.profitPurcAmt;
        this.totalStockAmount += item.stockPurcAmt;
        this.totalTableAmount += item.tablePurcAmt;
      });
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    handleCurrentChange(page) {
      this.searchParams.pageNum = page;
      this.loadStep2Data();
    },
    handleFilterTypeChange(type) {
      console.log('切换筛选类型:', type);
      this.filterType = type;
      this.$nextTick(() => {
        if (type === 'diff') {
          const count = type === 'diff' ? this.diffDrugCount : this.manualDiffDrugCount;
          this.$message.info(`已切换到差异药品视图，共 ${count} 条差异记录`);
        } else {
          this.$message.info('已切换到全部药品视图');
        }
      });
    },
    isDiffDrug(item) {
      if (!item) return false;
      const profitPacCnt = Number(item.profitPacCnt) || 0;
      const profitMinCnt = Number(item.profitMinCnt) || 0;
      return profitPacCnt !== 0 || profitMinCnt !== 0;
    },
    getDiffDrugTip(item) {
      const profitPacCnt = Number(item.profitPacCnt) || 0;
      const profitMinCnt = Number(item.profitMinCnt) || 0;
      if (profitPacCnt !== 0 && profitMinCnt !== 0) {
        return `包装单位盈亏: ${profitPacCnt}, 最小单位盈亏: ${profitMinCnt}`;
      } else if (profitPacCnt !== 0) {
        return `包装单位盈亏: ${profitPacCnt}`;
      } else if (profitMinCnt !== 0) {
        return `最小单位盈亏: ${profitMinCnt}`;
      }
      return '无差异';
    },
    async handleQuantityBlur(item, type) {
      console.log('数量输入框失去焦点:', item.drugName, type);
      this.calculateProfitLoss(item);
      await this.saveItemChange(item, type);
    },
    async saveItemChange(item, type) {
      try {
        const params = {
          id: item.id,
          stockMinCnt: Number(item.tableMinCnt) || 0,
          stockPacCnt: Number(item.tablePacCnt) || 0,
          drugId: item.drugId,
          drugCode: item.drugCode,
          stockType: this.selectedWarehouseType
        };
        console.log('保存盘点数量修改:', params);
        const res = await stockEditDetail(params);
        if (res.code === 200) {
          this.$message.success('盘点数量更新成功');
          item.originalMinCnt = item.tableMinCnt;
          item.originalPacCnt = item.tablePacCnt;
        } else {
          this.$message.error(res.message || '更新盘点数量失败');
          if (type === 'min') {
            item.tableMinCnt = item.originalMinCnt;
          } else if (type === 'pac') {
            item.tablePacCnt = item.originalPacCnt;
          }
          this.calculateProfitLoss(item);
        }
      } catch (error) {
        console.error('更新盘点数量失败:', error);
        this.$message.error('更新盘点数量失败，请重试');
        if (type === 'min') {
          item.tableMinCnt = item.originalMinCnt;
        } else if (type === 'pac') {
          item.tablePacCnt = item.originalPacCnt;
        }
        this.calculateProfitLoss(item);
      } finally {
        this.editingRows.delete(item.id);
      }
    },
    handleClearAll() {
      this.$confirm('确定要清空所有盘点数据吗？', '警告', {
        type: 'warning'
      }).then(() => {
        this.inventoryItems = [];
        this.$message.success('清空成功');
      });
    },
    editItem(row) {
      const copy = JSON.parse(JSON.stringify(row));
      if (!copy.traceDetails) copy.traceDetails = [];
      copy.isBar = row.isBar || 0;
      this.validateTraceCount(copy);
      this.manualTableItems = [copy];
      this.editingRowId = row.id;
      this.isEditManual = true;
      this.manualInputDialogVisible = true;
      this.manualSearchParams.drugName = "";
    },
    handlePrint() {
      this.$message.info('打印功能开发中');
    },
    handleExport() {
      this.$message.info('导出功能开发中');
    },
    handleHistory() {
      this.$message.info('历史单据功能开发中');
    },
    handleModify() {
      this.$confirm('确定要修改当前盘点结果吗？', '提示', {
        type: 'warning'
      }).then(() => {
        this.currentStep = 2;
        this.$message.success('已返回修改模式');
      });
    },
    async handleDetail(row) {
      try {
        this.detailLoading = true;
        this.detailDialogVisible = true;
        this.currentDetail = row;
        console.log(':', JSON.parse(JSON.stringify(row)));
        const detailParams = {
          id: row.id,
          stockType: row.stockType || this.queryForm.stockType || 1
        };
        console.log('调用detailStock接口参数:', detailParams);
        const res = await detailStock(detailParams);
        if (res.code === 200) {
          let detailData = res.data.list || [];
          if (!Array.isArray(detailData)) {
            if (detailData.drugId || detailData.drugName) {
              detailData = [detailData];
            } else {
              detailData = detailData.list || detailData.content || detailData.details || [];
            }
          }
          this.detailItems = detailData;
          console.log(this.detailItems, 'this.detailItems');
          if (detailData.length > 0) {
            const firstItem = detailData[0];
            this.currentDetail = {
              ...row,
              drugName: firstItem.drugName || row.drugName,
              manuLotnum: firstItem.manuLotnum || row.manuLotnum,
              prodentpName: firstItem.prodentpName || row.prodentpName,
              profitPurcSumAmt: firstItem.profitPurcSumAmt || row.profitPurcSumAmt,
              profitSaleSumAmt: firstItem.profitSaleSumAmt || row.profitSaleSumAmt
            };
          }
          this.$message.success(`详情数据加载成功，共 ${this.detailItems.length} 条记录`);
        } else {
          console.warn('detailStock接口返回错误:', res.message);
          await this.tryAlternativeDetailApi(row);
        }
      } catch (error) {
        console.error('加载详情数据失败:', error);
        this.detailItems = [];
        this.$message.error('加载详情失败: ' + (error.message || '请检查网络连接或联系管理员'));
        await this.tryAlternativeDetailApi(row);
      } finally {
        this.detailLoading = false;
      }
    },
    async tryAlternativeDetailApi(row) {
      try {
        console.log('尝试备用详情接口方案');
        const stockInfoParams = {
          id: row.id,
          stockType: row.stockType || this.queryForm.stockType || 1
        };
        console.log('备用方案参数（stockInfo）:', stockInfoParams);
        const stockInfoRes = await stockInfo(stockInfoParams);
        if (stockInfoRes.code === 200) {
          let data = stockInfoRes.data || [];
          if (!Array.isArray(data)) {
            data = data.content || data.list || data.details || [];
          }
          this.detailItems = data;
          this.$message.success('详情数据加载成功（备用方案）');
          return;
        }
        this.detailItems = [];
        this.$message.warning('获取详情数据失败，但已显示界面');
      } catch (error) {
        console.error('备用方案也失败:', error);
        this.detailItems = [];
        this.$message.info('详情数据加载失败，界面已显示');
      }
    },
    showDetailErrorInfo(error) {
      if (error.message && error.message.includes('不能为空')) {
        this.$message.warning('接口参数不完整，请联系管理员检查数据配置');
      } else if (error.message && error.message.includes('网络')) {
        this.$message.warning('网络连接异常，请检查网络设置');
      }
    },
    async handleEdit(row) {
      if (row.status !== 0) {
        this.$message.warning('只有草稿状态的盘点任务可以修改');
        return;
      }
      try {
        this.currentInventoryId = row.id;
        console.log('进入修改模式，设置当前任务ID:', this.currentInventoryId);
        this.currentStep = 2;
        this.currentStatus = row.status;
        this.isEditMode = true;
        this.selectedWarehouseType = row.stockType;
        this.searchParams.stockType = row.stockType;
        await this.loadStep2Data();
        this.$message.success(`进入修改模式：${row.docNum}`);
      } catch (error) {
        console.error('进入修改模式失败:', error);
        this.$message.error('加载修改数据失败');
      }
    },
    async handleSubmit(row) {
      if (row.status !== 0) {
        this.$message.warning('只有草稿状态的盘点任务可以提交');
        return;
      }
      try {
        await this.$confirm('确定要提交此盘点任务吗？提交后将进入审核流程', '提示', {
          type: 'warning'
        });
        const res = await toStockExamine({ id: row.id, status: 1 });
        console.log('提交审核接口响应:', res);
        if (res.code === 200) {
          this.$message.success('提交成功');
          this.loadStockList();
        } else {
          const errorMessage = res.message || '提交失败';
          this.$message.error(`提交失败: ${errorMessage}`);
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('提交盘点任务失败:', error);
          this.$message.error('提交失败');
        }
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确定要删除盘点任务"${row.docNum}"吗？`, '提示', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        });
        const res = await deleteStock({ id: row.id });
        if (res.code === 200) {
          this.$message.success('盘点任务删除成功');
          this.loadStockList();
        } else {
          this.$message.error(res.message || '删除盘点任务失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除盘点任务失败:', error);
          this.$message.error('删除失败，请重试');
        }
      }
    },
    async handleAudit(row) {
      const currentStatus = Number(row.status);
      if (currentStatus !== 1) {
        const statusText = this.getStatusText(currentStatus);
        this.$message.warning(`当前状态"${statusText}"不允许进行审核操作`);
        return;
      }
      try {
        await this.$confirm('审核生单后将生成盘盈/盘亏出入库单，是否继续？', '提示', {
          type: 'warning',
          confirmButtonText: '确定审核生单',
          cancelButtonText: '取消'
        });
        this.auditLoading = true;
        const auditRes = await toStockExamine({
          id: row.id,
          status: 2,
          stockType: row.stockType
        });
        if (auditRes.code !== 200) {
          throw new Error(auditRes.message || '审核失败');
        }
        const genRes = { code: 200, message: '生成单据成功' };
        if (genRes.code === 200) {
          this.$message.success('审核生单成功，已生成草稿单据');
          this.loadStockList();
          this.currentStep = 1;
        } else {
          this.$message.error(genRes.message || '生成单据失败');
        }
      } catch (error) {
        console.error('审核生单失败:', error);
        this.$message.error(error.message || '操作失败');
      } finally {
        this.auditLoading = false;
      }
    },
    handleScanTraceCode() {
      const traceCode = this.traceCodeInput.trim();
      if (!traceCode) {
        this.$message.warning('请输入或扫描追溯码');
        return;
      }
      this.traceCodeLoading = true;
      msfxCodeDetail({ codes: traceCode }).then(res => {
        if (res.code !== 200 || !res.data || res.data.length === 0) {
          this.$message.error('未查询到该追溯码的信息');
          return;
        }
        const traceData = res.data[0];
        this.currentTraceDetailData = traceData;
        this.currentTraceCode = traceCode;
        this.traceCodeDetailDialogVisible = true;
      }).catch(error => {
        console.error('追溯码查询失败:', error);
        this.$message.error('查询失败，请检查网络');
      }).finally(() => {
        this.traceCodeLoading = false;
        this.traceCodeInput = '';
      });
    },
    addOrMergeTraceDrug(drugInfo) {
      // 未使用
    },
    buildManualItemFromDrugInfo(drugInfo) {
      // 未使用
    },
    async loadTaskTableData() {
      try {
        this.taskTableLoading = true;
        console.log('加载已盘任务数据，药库类型:', this.taskTableStockType);
        const params = {
          stockType: this.taskTableStockType,
          pageNum: 1,
          pageSize: 50,
          status: 2
        };
        const res = await infoIsStocked(params);
        if (res.code === 200) {
          const data = res.data || [];
          this.taskTableItems = data.content || data.list || data || [];
          console.log('已盘任务数据加载成功:', this.taskTableItems.length, '条记录');
        } else {
          console.error('加载已盘任务数据失败:', res.message);
          this.$message.error(res.message || '加载已盘任务数据失败');
          this.taskTableItems = [];
        }
      } catch (error) {
        console.error('加载已盘任务数据失败:', error);
        this.$message.error('加载已盘任务数据失败');
        this.taskTableItems = [];
      } finally {
        this.taskTableLoading = false;
      }
    },
    openTraceManage(row) {
      const latestRow = this.inventoryItems.find(item => item.id === row.id);
      if (!latestRow) {
        return;
      }
      this.currentTraceRow = latestRow;
      const pacCnt = Number(latestRow.pacCnt) || 1;
      const traceDetails = latestRow.traceDetails || [];
      this.batchTraceCodes = traceDetails.map(t => ({
        code: t.code || '',
        drugName: latestRow.drugName || '',
        entName: latestRow.prodentpName || '',
        pacCnt: Number(t.pacCnt) || pacCnt,
        availableQty: Number(t.availableQty) || 0,
        editing: !t.code
      }));
      if (this.batchTraceCodes.length === 0) {
        this.$message.warning('当前药品没有追溯码，请添加');
      }
      this.traceManageDialogVisible = true;
    },
    handleAvailableQtyChange(row, index) {
      const pacCnt = Number(row.pacCnt) || 1;
      let newQty = Number(row.availableQty) || 0;
      if (newQty < 0) newQty = 0;
      row.availableQty = newQty;
      const currentCode = row.code;
      const totalQty = newQty;
      if (totalQty === 0) {
        this.batchTraceCodes.splice(index, 1);
        return;
      }
      const rows = Math.ceil(totalQty / pacCnt);
      const newRows = [];
      let remaining = totalQty;
      for (let i = 0; i < rows; i++) {
        const qty = Math.min(remaining, pacCnt);
        const code = (i === 0 && currentCode) ? currentCode : '';
        newRows.push({
          code: code,
          drugName: row.drugName,
          entName: row.entName,
          pacCnt: pacCnt,
          availableQty: qty,
          editing: !code
        });
        remaining -= qty;
      }
      this.batchTraceCodes.splice(index, 1, ...newRows);
      this.$forceUpdate();
    },
    async addTraceCodeToBatch() {
      const newCode = this.newTraceCode.trim();
      if (!newCode) {
        this.$message.warning('请输入追溯码');
        return;
      }
      if (this.batchTraceCodes.some(t => t.code === newCode)) {
        this.$message.warning('该追溯码已存在，请勿重复添加');
        return;
      }
      try {
        const res = await msfxCodeDetail({ codes: newCode });
        if (res.code === 200 && res.data && res.data.length > 0) {
          const detailData = res.data[0];
          const drugEntBase = detailData?.drugEntBaseDTO || {};
          const pUserEnt = detailData?.pUserEntDTO || {};
          const traceDrugName = drugEntBase.physicName || '';
          const tracePrepnSpec = drugEntBase.prepnSpec || '';
          const tracePacCnt = Number(tracePrepnSpec) || 0;
          const row = this.currentTraceRow || {};
          const rowDrugName = row.drugName || '';
          const rowPacCnt = Number(row.pacCnt) || 1;
          if (traceDrugName.trim().toLowerCase() !== rowDrugName.trim().toLowerCase()) {
            this.$message.error(`追溯码对应的药品名称（${traceDrugName}）与当前药品（${rowDrugName}）不一致，无法添加`);
            return;
          }
          if (tracePacCnt !== rowPacCnt) {
            this.$message.error(`追溯码对应的包装数量（${tracePacCnt}）与当前药品的包装数量（${rowPacCnt}）不一致，无法添加`);
            return;
          }
          this.batchTraceCodes.push({
            code: newCode,
            drugName: traceDrugName || row.drugName || '',
            entName: pUserEnt.entName || row.prodentpName || '',
            pacCnt: rowPacCnt,
            availableQty: 0,
            editing: false
          });
          this.$message.success('已添加追溯码');
          this.newTraceCode = '';
        } else {
          this.$message.warning('未查询到该追溯码的信息，无法添加');
        }
      } catch (error) {
        console.error('查询追溯码详情失败:', error);
        this.$message.error('查询追溯码失败，请检查网络或重试');
      }
    },
    saveTraceCodeEdit(row) {
      const newCode = row.code.trim();
      if (!newCode) {
        this.$message.warning('追溯码不能为空');
        return;
      }
      const duplicate = this.batchTraceCodes.some(t => t !== row && t.code === newCode);
      if (duplicate) {
        this.$message.warning('该追溯码已存在');
        return;
      }
      row.editing = false;
      this.$message.success('追溯码已更新');
    },
    removeTraceCode(row) {
      const index = this.batchTraceCodes.indexOf(row);
      if (index !== -1) {
        this.batchTraceCodes.splice(index, 1);
        this.updateTraceRowSummary();
      }
    },
    updateTraceRowSummary() {
      // 可更新汇总
    },
    async confirmTraceManage() {
      const emptyCode = this.batchTraceCodes.some(t => !t.code || t.code.trim() === '');
      if (emptyCode) {
        this.$alert('当前有未填写完成的追溯码，请仔细检查并确认！', '提示', {
          confirmButtonText: '我知道了',
          type: 'warning'
        });
        return;
      }
      let totalQty = 0;
      const pacCnt = Number(this.currentTraceRow.pacCnt) || 1;
      this.batchTraceCodes.forEach(t => {
        totalQty += Number(t.availableQty) || 0;
      });
      this.currentTraceRow.stockPacCnt = Math.floor(totalQty / pacCnt);
      this.currentTraceRow.stockMinCnt = totalQty % pacCnt;
      this.currentTraceRow.traceDetails = this.batchTraceCodes.map(t => ({
        code: t.code,
        availableQty: Number(t.availableQty) || 0,
        pacCnt: pacCnt
      }));
      const hasEmptyInDetails = this.currentTraceRow.traceDetails.some(d => !d.code || d.code.trim() === '');
      if (hasEmptyInDetails) {
        this.$alert('当前有未填写完成的追溯码，请仔细检查并确认！', '提示', {
          confirmButtonText: '我知道了',
          type: 'warning'
        });
        return;
      }
      const tracCodeJson = this.buildTracCodeJson(this.currentTraceRow);
      const params = {
        id: this.currentTraceRow.id,
        stockId: this.currentInventoryId,
        drugId: this.currentTraceRow.drugId,
        drugCode: this.currentTraceRow.drugCode,
        stockMinCnt: Number(this.currentTraceRow.stockMinCnt) || 0,
        stockPacCnt: Number(this.currentTraceRow.stockPacCnt) || 0,
        stockType: this.selectedWarehouseType,
        tracCodeJson: tracCodeJson
      };
      this.traceSaveLoading = true;
      try {
        const res = await stockEditDetail(params);
        if (res.code === 200) {
          this.$message.success('追溯码和盘点数量已保存');
          await this.loadStep2Data();
          this.syncManualTableItems();
          this.traceManageDialogVisible = false;
          this.$forceUpdate();
        } else {
          this.$message.error(res.message || '保存失败，请重试');
        }
      } catch (error) {
        console.error('保存追溯码失败:', error);
        this.$message.error('保存失败，请检查网络或联系管理员');
      } finally {
        this.traceSaveLoading = false;
      }
    },
    async confirmTraceCodeDetail() {
      if (!this.currentTraceDetailData) {
        this.$message.warning('追溯码数据无效');
        return;
      }
      const traceData = this.currentTraceDetailData;
      const traceCode = this.currentTraceCode;
      const drugEntBase = traceData.drugEntBaseDTO || {};
      const produceInfoList = traceData.codeProduceInfoDTO?.produceInfoList || [];
      const produceInfo = produceInfoList[0] || {};
      const pUserEnt = traceData.pUserEntDTO || {};

      const drugName = drugEntBase.physicName;
      const batchNo = produceInfo.batchNo;
      const manufacturer = pUserEnt.entName || '';

      if (!drugName) {
        this.$message.error('无法从追溯码中解析药品名称');
        return;
      }

      try {
        const matchParams = {
          msg: drugName,
          stockType: this.selectedWarehouseType
        };
        const matchRes = await getValueStock(matchParams);
        if (matchRes.code !== 200) {
          this.$message.error('获取库存数据失败，请重试');
          return;
        }
        const stockList = matchRes.data || [];
        const matched = stockList.find(item => {
          const nameMatch = item.drugName && item.drugName.trim().toLowerCase() === drugName.trim().toLowerCase();
          const batchMatch = batchNo ? (item.manuLotnum && item.manuLotnum.trim() === batchNo.trim()) : true;
          const manuMatch = manufacturer ? (item.prodentpName && item.prodentpName.trim() === manufacturer.trim()) : true;
          return nameMatch && batchMatch && manuMatch;
        });

        if (!matched) {
          this.$alert(
            `当前库存暂无“${drugName}”（批次号：${batchNo || '无'}，厂家：${manufacturer || '未知'}）的库存药品，请检查库存！或者尝试手动入库。`,
            '提示',
            { confirmButtonText: '我知道了', type: 'warning' }
          );
          this.traceCodeDetailDialogVisible = false;
          return;
        }

        const detailParams = {
          stockType: this.selectedWarehouseType,
          drugName: matched.drugName,
          manuLotnum: matched.manuLotnum || '',
          prodentpName: matched.prodentpName || ''
        };
        const detailRes = await stockInfo(detailParams);
        if (detailRes.code !== 200 || !detailRes.data || detailRes.data.length === 0) {
          this.$message.error('获取药品库存明细失败');
          return;
        }
        const detail = detailRes.data[0];
        const realId = detail.id;
        const realStockQty = detail.stockMinCnt || 0;
        // 修复：确保 drugType 字段被正确传递
        const drugType = detail.drugType !== undefined && detail.drugType !== null
          ? detail.drugType
          : (matched.drugType || 0);
		const tableMin = Number(detail.tableMinCnt) !== undefined ? Number(detail.tableMinCnt) : Number(detail.stockMinCnt) || 0;
		const tablePac = Number(detail.tablePacCnt) !== undefined ? Number(detail.tablePacCnt) : Number(detail.stockPacCnt) || 0;
		
        const newItem = {
          id: realId,
          drugId: detail.drugId || matched.drugId,
          drugCode: detail.drugCode || matched.drugCode || '',
          drugName: detail.drugName || matched.drugName,
          drugType: drugType,
          drugSpec: detail.drugSpec || matched.drugSpec || '',
          manuLotnum: detail.manuLotnum || batchNo || '',
          prodentpName: detail.prodentpName || manufacturer || '',
          minUnt: detail.dosUnt || '',
          pacUnt: detail.pacUnt || '',
          stockMinCnt: tableMin,
		  stockPacCnt: tablePac,
		  tableMinCnt: tableMin,
		  tablePacCnt: tablePac,
          minPurcPric: Number(detail.minPurcPric) || 0,
          maxPurcPric: Number(detail.maxPurcPric) || 0,
          minSalePric: Number(detail.minSalePric) || 0,
          maxSalePric: Number(detail.maxSalePric) || 0,
		  
          isManual: true,
          originalMinCnt: realStockQty,
          originalPacCnt: 0,
          traceDetails: [{ code: traceCode }],
          fromTrace: true,
          hasTrace: true,
          isBar: 1,
        };
        const drugEntBase2 = traceData.drugEntBaseDTO || {};
        const pUserEnt2 = traceData.pUserEntDTO || {};
        newItem.traceDetails = [{
          code: traceCode,
          availableQty: 0,
          drugName: drugEntBase2.physicName || drugName,
          prepnSpec: drugEntBase2.prepnSpec || '',
          approvalLicenceNo: drugEntBase2.approvalLicenceNo || '',
          entName: pUserEnt2.entName || manufacturer || ''
        }];
        this.calculateManualProfitLoss(newItem);
        this.manualTableItems.push(newItem);
        this.$message.success('药品已添加到盘点表，请通过追溯码按钮维护具体数量');

        this.traceCodeDetailDialogVisible = false;
        this.currentTraceDetailData = null;
        this.currentTraceCode = '';
      } catch (error) {
        console.error('匹配药品失败:', error);
        this.$message.error('匹配药品失败，请重试');
      }
    },
    buildTracCodeJson(row) {
      if (!row.traceDetails || row.traceDetails.length === 0) {
        return '';
      }
      const tracList = row.traceDetails.map(item => ({
        barcode: item.code,
        chgCnt: Number(item.availableQty) || 0,
        manuLotnum: row.manuLotnum || ''
      }));
      return JSON.stringify(tracList);
    },
    async validateGoodsStock(goods, purcType = 2, allotNum = 1) {
      try {
        const params = {
          stockType: this.selectedWarehouseType,
          drugName: goods.drugName,
          manuLotnum: goods.manuLotnum || '',
          prodentpName: goods.prodentpName || ''
        };
        const res = await stockInfo(params);
        if (res.code === 200 && res.data && res.data.length > 0) {
          const detail = res.data[0];
          let realPurcCnt = 0;
          if (purcType === 1) {
            realPurcCnt = detail.stockMinCnt || 0;
          } else {
            realPurcCnt = detail.stockPacCnt || 0;
          }
          return {
            code: 200,
            data: {
              realPurcCnt: realPurcCnt,
              purcCnt: realPurcCnt,
              maxQuantity: realPurcCnt,
              minPurcPric: detail.minPurcPric || 0,
              minSalePric: detail.minSalePric || 0,
              purcPric: detail.maxPurcPric || 0,
              salePric: detail.maxSalePric || 0,
              stockMinCnt: detail.stockMinCnt || 0,
              stockPacCnt: detail.stockPacCnt || 0,
              drugSpec: detail.drugSpec || '',
              minUnt: detail.minUnt || '',
              pacUnt: detail.pacUnt || '',
              dosUnt: detail.dosUnt || '',
              drugCode: detail.drugCode || '',
              prodentpName: detail.prodentpName || '',
              manuLotnum: detail.manuLotnum || ''
            }
          };
        } else {
          return { code: 404, message: '未找到库存信息' };
        }
      } catch (error) {
        console.error('验证库存失败:', error);
        return { code: 500, message: '验证库存异常' };
      }
    },
    extractNumberFromString(str) {
      if (!str) return 1;
      const match = str.match(/\d+(\.\d+)?/);
      return match ? parseFloat(match[0]) : 1;
    },
    matchDrugInLocal(drugName, batchNo, pacCnt) {
      return null;
    },
    handleCarryOver(row) {
      if (row.status !== 2) {
        this.$message.warning('只有已审核状态的盘点任务可以结转');
        return;
      }
      this.$confirm('确定要进行结转操作吗？结转后将更新库存数据', '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          const res = await toStockExamine({ id: row.id, status: 3 });
          if (res.code === 200) {
            this.$message.success('结转成功');
            this.loadStockList();
          } else {
            this.$message.error(res.message || '结转失败');
          }
        } catch (error) {
          console.error('结转盘点任务失败:', error);
          this.$message.error('结转失败');
        }
      });
    },
    handleReverseCarryOver(row) {
      if (row.status !== 3) {
        this.$message.warning('只有已结转状态的盘点任务可以反结转');
        return;
      }
      this.$confirm('确定要进行反结转操作吗？此操作将撤销结转，请谨慎操作！', '警告', {
        type: 'warning',
        confirmButtonText: '确定反结转',
        cancelButtonText: '取消',
        confirmButtonClass: 'el-button--danger'
      }).then(async () => {
        try {
          const res = await toStockExamine({ id: row.id, status: 4 });
          if (res.code === 200) {
            this.$message.success('反结转成功');
            this.loadStockList();
          } else {
            this.$message.error(res.message || '反结转失败');
          }
        } catch (error) {
          console.error('反结转失败:', error);
          this.$message.error('反结转失败');
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.drug-inventory {
  height: 100%;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .step-nav {
    background: white;
    padding: 20px;
    margin-bottom: 8px;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    ::v-deep .el-steps {
      .el-step__head.is-process {
        color: #409EFF;
        border-color: #409EFF;
        .el-step__icon {
          background: #409EFF;
          color: white;
          border: none;
        }
        .el-step__icon-inner {
          font-weight: bold;
          font-size: 16px;
        }
      }
      .el-step__head.is-success {
        color: #67C23A;
        border-color: #67C23A;
        .el-step__icon {
          background: #67C23A;
          color: white;
          border: none;
        }
        .el-step__icon-inner {
          font-weight: bold;
          font-size: 18px;
          &::before {
            content: "✓";
          }
        }
        .el-step__icon-inner:not(:before) {
          display: none;
        }
      }
      .el-step__head.is-wait {
        color: #C0C4CC;
        border-color: #C0C4CC;
        .el-step__icon {
          background: white;
          color: #C0C4CC;
          border: 2px solid #C0C4CC;
        }
        .el-step__icon-inner {
          font-weight: normal;
          font-size: 14px;
        }
      }
      .el-step__title.is-process {
        color: #409EFF;
        font-weight: 600;
      }
      .el-step__title.is-success {
        color: #67C23A;
      }
      .el-step__title.is-wait {
        color: #C0C4CC;
      }
      .el-step__description.is-process {
        color: #409EFF;
      }
      .el-step__description.is-success {
        color: #67C23A;
      }
      .el-step__description.is-wait {
        color: #909399;
      }
      .el-step__line {
        background-color: #E4E7ED;
      }
      .el-step.is-horizontal .el-step__line {
        top: 15px;
      }
    }
  }

  .step-content {
    flex: 1;
    background: white;
    border-radius: 4px;
    margin: 0 8px 8px 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 6px;
  }

  /* 第一步 */
  .step1 {
    display: flex;
    flex-direction: column;
    height: 100%;

    .table-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 15px;
      border-bottom: 1px solid #eee;
      .header-actions {
        display: flex;
        gap: 10px;
        align-items: center;
      }
    }

    .filter-container {
      display: flex;
      align-items: center;
      padding: 15px;
      background: #f8f9fa;
      border-radius: 4px;
      border: 1px solid #ebeef5;
      margin-bottom: 15px;
      min-height: 60px;
      .filter-title {
        margin-right: 20px;
        flex-shrink: 0;
        .title-text {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
          line-height: 32px;
          display: flex;
          align-items: center;
          height: 100%;
        }
      }
      .query-form {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin-bottom: 0;
        flex: 1;
        gap: 15px;
        height: 100%;
        ::v-deep .el-form-item {
          margin-bottom: 0 !important;
          display: flex !important;
          align-items: center !important;
          height: 32px !important;
          margin-right: 0 !important;
          .el-form-item__label {
            line-height: 32px !important;
            height: 32px !important;
            padding: 0 10px 0 0 !important;
            text-align: right;
            color: #606266;
            font-size: 14px;
            font-weight: normal;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            float: none !important;
          }
          .el-form-item__content {
            display: flex !important;
            align-items: center !important;
            line-height: 32px !important;
            height: 32px !important;
            margin-left: 0 !important;
            flex: 1;
            .el-input, .el-select, .el-cascader, .el-date-editor {
              width: 100%;
              .el-input__inner {
                height: 32px !important;
                line-height: 30px !important;
                border-radius: 4px;
                font-size: 14px;
              }
              .el-input__icon, .el-input__suffix {
                line-height: 32px;
                height: 32px;
                display: flex;
                align-items: center;
              }
            }
            .el-input-group {
              display: flex;
              align-items: stretch;
              .el-input__inner {
                flex: 1;
              }
              .el-input-group__append {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 12px;
                background: #f5f7fa;
                border: 1px solid #dcdfe6;
                border-left: 0;
                .el-button {
                  margin: 0;
                  padding: 0;
                  border: none;
                  background: transparent;
                  height: 30px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  &:hover {
                    background-color: #e4e7ed;
                  }
                  i {
                    font-size: 14px;
                    line-height: 1;
                  }
                }
              }
            }
          }
        }
        .form-item-center:not(:has(.el-form-item__label)) {
          ::v-deep .el-form-item__content {
            margin-left: 0 !important;
          }
        }
      }
    }

    .table-container {
      flex: 1;
      min-height: 300px;
      overflow: hidden;
      display: flex;
      flex-direction: column;

      .custom-summary-row {
        background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
        border: 2px solid #1890ff;
        border-top: none;
        padding: 12px 16px;
        margin-top: -1px;
        position: relative;
        z-index: 10;
        .summary-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
          .summary-left {
            display: flex;
            align-items: center;
            gap: 20px;
            flex: 1;
            .summary-label {
              font-weight: 700;
              color: #1890ff;
              font-size: 16px;
              background: #1890ff;
              color: white;
              padding: 4px 12px;
              border-radius: 4px;
              min-width: 60px;
              text-align: center;
            }
            .summary-item {
              display: flex;
              align-items: center;
              gap: 8px;
              label {
                color: #666;
                font-size: 14px;
                font-weight: 500;
                white-space: nowrap;
              }
              .summary-amount {
                font-weight: 700;
                color: #1890ff;
                font-family: 'Courier New', monospace;
                font-size: 15px;
                background: rgba(24, 144, 255, 0.1);
                padding: 4px 10px;
                border-radius: 4px;
                border: 1px solid #91d5ff;
                min-width: 120px;
                text-align: right;
              }
            }
          }
          .summary-right {
            flex-shrink: 0;
            .summary-pagination {
              ::v-deep .el-pagination {
                padding: 0;
                .el-pagination__total, .el-pagination__sizes, .el-pagination__jump {
                  margin: 0;
                }
                .btn-prev, .btn-next, .el-pager li {
                  margin: 0 2px;
                  min-width: 28px;
                  height: 28px;
                  line-height: 26px;
                }
              }
            }
          }
        }
      }

      ::v-deep .el-table {
        flex: 1;
        width: 100%;
        .el-table__body-wrapper {
          overflow-x: auto;
        }
        .el-table__body {
          min-width: 100%;
        }
        .el-tag {
          border-radius: 12px;
          font-size: 12px;
          height: 24px;
          line-height: 22px;
          padding: 0 8px;
        }
        .operation-buttons {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          min-height: 32px;
          .el-button {
            margin: 0;
            padding: 3px 8px;
            font-size: 12px;
            min-height: 24px;
            line-height: 1;
            border: none;
            background: transparent;
            transition: all 0.2s;
            &.el-button--text {
              color: #409EFF;
              &:hover {
                color: #66b1ff;
                background-color: rgba(64, 158, 255, 0.1);
              }
            }
            &.delete-btn {
              color: #f56c6c;
              &:hover {
                color: #f78989;
                background-color: rgba(245, 108, 108, 0.1);
              }
            }
            &.reverse-btn {
              color: #e6a23c;
              &:hover {
                color: #ebb563;
                background-color: rgba(230, 162, 60, 0.1);
              }
            }
            &:disabled {
              color: #c0c4cc;
              cursor: not-allowed;
              background: transparent;
            }
          }
        }
      }

      .empty-data {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
        color: #909399;
        background: white;
        border-radius: 4px;
        border: 1px solid #ebeef5;
        .el-empty {
          padding: 40px 0;
          .el-empty__image {
            width: 120px;
            height: 120px;
          }
          .el-empty__description {
            margin-top: 20px;
            color: #909399;
            font-size: 14px;
          }
        }
      }
    }

    .pagination {
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px solid #eee;
      display: flex;
      justify-content: flex-end;
      background: white;
      ::v-deep .el-pagination {
        .el-pagination__sizes {
          margin-right: 10px;
        }
        .el-pagination__total {
          margin-right: 10px;
          color: #606266;
        }
      }
    }
  }

  /* 第二步 */
  .step2 {
    .step2-header {
      margin-bottom: 12px;
      border-bottom: 1px solid #eee;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;

      .header-left {
        display: flex;
        align-items: center;
        gap: 16px;
        .task-title {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
          margin-right: 8px;
        }
        .action-buttons {
          display: flex;
          gap: 8px;
        }
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 10px;
        .filter-buttons {
          display: flex;
          align-items: center;
          .el-button {
            margin: 0;
          }
          .el-badge {
            position: absolute;
            top: -8px;
            right: -8px;
            transform: scale(0.8);
          }
        }
        .filter-info {
          .info-text {
            font-size: 13px;
            color: #606266;
          }
          .warning-text {
            color: #e6a23c;
          }
        }
      }
    }

    .dual-tables-container {
      display: flex;
      gap: 20px;
      margin-bottom: 20px;

      &.task-tables-show {
        .main-table-container {
          width: 60%;
        }
        .task-table-container {
          width: 40%;
          display: block;
        }
      }

      .main-table-container, .task-table-container {
        display: flex;
        flex-direction: column;
        background: #f8f9fa;
        border-radius: 4px;
        padding: 15px;
        width: 100%;
        border: 1px solid #ebeef5;

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          h4 {
            margin: 0;
            color: #303133;
            font-size: 16px;
          }
          .table-actions {
            display: flex;
            align-items: center;
            gap: 10px;
            .table-count {
              font-size: 14px;
              color: #606266;
            }
          }
        }

        .table-wrapper {
          flex: 1;
          min-height: 400px;
          position: relative;
          ::v-deep .el-table {
            .el-table__body-wrapper {
              overflow-x: auto;
            }
            .el-table__body {
              min-width: 100%;
            }
            .quantity-input .el-input__inner {
              text-align: center;
            }
            .number-text, .price-text, .id-text {
              font-family: 'Courier New', monospace;
            }
            .profit-text {
              color: #67C23A;
              font-weight: 500;
            }
            .loss-text {
              color: #F56C6C;
              font-weight: 500;
            }
          }
          .empty-data {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.95);
            z-index: 10;
          }
        }

        .pagination {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #ebeef5;
        }
      }

      .task-table-container {
        display: none;
      }
    }

    .step-actions {
      display: flex;
      justify-content: center;
      gap: 15px;
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px solid #eee;
    }
  }

  /* 第三步 */
  .step3 {
    .inventory-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 1px solid #eee;
      .header-left {
        display: flex;
        align-items: center;
        gap: 15px;
        .document-number {
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
        .el-tag {
          border-radius: 12px;
          font-size: 12px;
          height: 24px;
          line-height: 22px;
          padding: 0 8px;
        }
      }
    }

    .search-bar {
      display: flex;
      align-items: center;
      gap: 15px;
      margin-bottom: 20px;
      padding: 15px;
      background: #f8f9fa;
      border-radius: 4px;
      .search-input {
        width: 300px;
      }
      .filter-buttons {
        display: flex;
        align-items: center;
        .el-button {
          margin: 0;
        }
        .el-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          transform: scale(0.8);
        }
      }
      .table-wrapper {
        position: relative;
      }
      .summary-info {
        display: flex;
        gap: 20px;
        font-size: 14px;
        color: #606266;
        span {
          padding: 4px 8px;
          background: white;
          border-radius: 4px;
          border: 1px solid #dcdfe6;
        }
      }
    }

    .inventory-table {
      ::v-deep .el-table {
        .el-table__body-wrapper {
          overflow-x: auto;
        }
        .el-table__body {
          min-width: 100%;
        }
        .profit-text {
          color: #67C23A;
          font-weight: 500;
        }
        .loss-text {
          color: #F56C6C;
          font-weight: 500;
        }
      }
      .table-footer {
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid #eee;
        .table-summary {
          margin-bottom: 15px;
          .summary-row {
            display: flex;
            justify-content: flex-end;
            gap: 120px;
            padding: 8px 0;
            font-weight: 600;
            span {
              min-width: 100px;
              text-align: right;
            }
          }
        }
      }
    }

    .action-buttons {
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px solid #eee;
      display: flex;
      justify-content: center;
      gap: 15px;
    }
  }

  /* 对话框通用样式 (穿透) */
  ::v-deep .el-dialog__wrapper .el-dialog {
    border-radius: 8px;
    overflow: hidden;
    .el-dialog__header {
      background: #f8f9fa;
      padding: 15px 20px;
      border-bottom: 1px solid #ebeef5;
      .el-dialog__title {
        font-size: 16px;
        font-weight: 600;
        color: #333;
      }
    }
    .el-dialog__body {
      padding: 20px;
      .el-form-item {
        margin-bottom: 20px;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
    .el-dialog__footer {
      padding: 15px 20px;
      border-top: 1px solid #ebeef5;
      background: #f8f9fa;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }
  }

  /* 手工录入模态框 (穿透) */
  ::v-deep .manual-input-dialog {
    .manual-input-content {
      .search-area {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-bottom: 16px;
        .shortcut-hint {
          margin-left: 15px;
          .hint-text {
            color: #909399;
            font-size: 13px;
          }
        }
      }
    }
  }

  /* 追溯码维护模态框 (自定义类 trace-code-wh) */
  ::v-deep .trace-code-wh {
    .el-dialog__header {
      background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
      padding: 15px 20px;
      border-radius: 8px 8px 0 0;
      .el-dialog__title {
        color: #fff;
      }
      .el-dialog__headerbtn .el-dialog__close {
        color: #fff;
        &:hover {
          color: #ffd700;
        }
      }
    }
  }

  /* 追溯码详情模态框 (自定义类 trace-code-detail-modal) */
  ::v-deep .trace-code-detail-modal {
    .el-dialog__header {
      background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
      padding: 15px 20px;
      border-radius: 8px 8px 0 0;
      .el-dialog__title {
        color: #fff;
      }
      .el-dialog__headerbtn .el-dialog__close {
        color: #fff;
        &:hover {
          color: #ffd700;
        }
      }
    }
    .trace-code-detail-container {
      padding: 10px 0;
      .trace-code-descriptions {
        ::v-deep .el-descriptions__label {
          background-color: #f5f7fa;
          width: 110px;
        }
        ::v-deep .el-descriptions__content {
          word-break: break-all;
        }
      }
    }
  }

  /* 响应式 */
  @media (max-width: 768px) {
    .step-nav {
      padding: 15px 10px;
    }
    .step-content {
      padding: 15px;
      margin: 0 4px 8px 4px;
    }
    .step1 {
      .filter-container {
        flex-direction: column;
        align-items: flex-start;
        .filter-title {
          margin-right: 0;
          margin-bottom: 10px;
          width: 100%;
        }
        .query-form {
          width: 100%;
          .form-item-center {
            width: 100%;
            margin-right: 0;
            margin-bottom: 10px;
            .el-select, .el-input {
              width: 100% !important;
            }
          }
        }
      }
      .table-container ::v-deep .el-table .operation-buttons {
        flex-direction: column;
        align-items: center;
        gap: 4px;
        min-height: auto;
        .el-button {
          width: 100%;
          text-align: center;
          padding: 4px 0;
          margin: 1px 0;
        }
      }
    }
    .step2 .dual-tables-container {
      flex-direction: column;
      &.task-tables-show .main-table-container,
      &.task-tables-show .task-table-container {
        width: 100%;
      }
    }
    .step3 {
      .search-bar {
        flex-direction: column;
        align-items: flex-start;
        .summary-info {
          width: 100%;
          justify-content: space-between;
        }
      }
      .table-footer .summary-row {
        gap: 60px !important;
      }
    }
  }

  @media (max-width: 480px) {
    .step1 .filter-container .query-form .form-item-center .el-form-item__label {
      padding: 0 0 5px 0;
      text-align: left;
      width: 100% !important;
      float: none;
    }
  }

  /* 表格通用样式优化 */
  ::v-deep .el-table {
    th {
      background-color: #f5f7fa;
      color: #333;
      font-weight: 600;
      font-size: 13px;
      white-space: nowrap;
      padding: 4px 0 !important;
    }
    .el-table__row {
      height: 20px !important;
      &:hover {
        background-color: #f5f7fa;
      }
    }
    .el-table__row--striped {
      background-color: #fafafa;
    }
    td {
      padding: 2px 0 !important;
      .cell {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.2;
        padding: 0 4px;
      }
    }
  }

  .el-button {
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    &:active {
      transform: translateY(1px);
    }
  }

  /* 滚动条 */
  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
}
</style>

<style lang="less">
/* ===== 全局对话框样式（不 scoped，确保对 body 下元素生效） ===== */

/* ---------- 通用对话框 ---------- */
.el-dialog__wrapper .el-dialog {
  border-radius: 8px;
  overflow: hidden;
}
.el-dialog__wrapper .el-dialog .el-dialog__header {
  background: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
}
.el-dialog__wrapper .el-dialog .el-dialog__header .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
.el-dialog__wrapper .el-dialog .el-dialog__body {
  padding: 20px;
}
.el-dialog__wrapper .el-dialog .el-dialog__body .el-form-item {
  margin-bottom: 20px;
}
.el-dialog__wrapper .el-dialog .el-dialog__body .el-form-item:last-child {
  margin-bottom: 0;
}
.el-dialog__wrapper .el-dialog .el-dialog__footer {
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
  background: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* ---------- 手工录入模态框 ---------- */
.el-dialog__wrapper .manual-input-dialog .manual-input-content .search-area {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.el-dialog__wrapper .manual-input-dialog .manual-input-content .search-area .shortcut-hint {
  margin-left: 15px;
}
.el-dialog__wrapper .manual-input-dialog .manual-input-content .search-area .shortcut-hint .hint-text {
  color: #909399;
  font-size: 13px;
}

/* ---------- 追溯码维护模态框 ---------- */
.el-dialog__wrapper .trace-code-wh .el-dialog__header {
  background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
  padding: 15px 20px;
  border-radius: 8px 8px 0 0;
}
.el-dialog__wrapper .trace-code-wh .el-dialog__header .el-dialog__title {
  color: #fff;
}
.el-dialog__wrapper .trace-code-wh .el-dialog__header .el-dialog__headerbtn .el-dialog__close {
  color: #fff;
}
.el-dialog__wrapper .trace-code-wh .el-dialog__header .el-dialog__headerbtn .el-dialog__close:hover {
  color: #ffd700;
}

/* ---------- 追溯码详情模态框 ---------- */
.el-dialog__wrapper .trace-code-detail-modal .el-dialog__header {
  background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
  padding: 15px 20px;
  border-radius: 8px 8px 0 0;
}
.el-dialog__wrapper .trace-code-detail-modal .el-dialog__header .el-dialog__title {
  color: #fff;
}
.el-dialog__wrapper .trace-code-detail-modal .el-dialog__header .el-dialog__headerbtn .el-dialog__close {
  color: #fff;
}
.el-dialog__wrapper .trace-code-detail-modal .el-dialog__header .el-dialog__headerbtn .el-dialog__close:hover {
  color: #ffd700;
}
.el-dialog__wrapper .trace-code-detail-modal .trace-code-detail-container {
  padding: 10px 0;
}
.el-dialog__wrapper .trace-code-detail-modal .trace-code-detail-container .trace-code-descriptions .el-descriptions__label {
  background-color: #f5f7fa;
  width: 110px;
}
.el-dialog__wrapper .trace-code-detail-modal .trace-code-detail-container .trace-code-descriptions .el-descriptions__content {
  word-break: break-all;
}
</style>